import {
  AttemptEventTypeDto,
  AttemptModeDto,
  AttemptStatusDto,
  AttemptTimelineKindDto,
  BulkOperationDto,
  CheckFailureDto,
  CheckStatusDto,
  CheckStepNameDto,
  ConversionProblemCodeDto,
  HistoryEventKindDto,
  ImportErrorCodeDto,
  MediaRejectionReasonDto,
  MistakeReviewDto,
  PoolSkipReasonDto,
  QuestionPurposeDto,
  QuestionRuleDto,
  QuestionTypeDto,
  ScoringRuleDto,
  SourceTypeDto,
  SurveyGapCodeDto,
  SurveyResponseStatusDto,
  SurveyStatusDto,
  TestGapCodeDto,
  TestPartKindDto,
  TestPoolWarningCodeDto,
  TestPurposeDto,
  TestStatusDto,
  TranslationStatusDto,
  VersionStatusDto,
} from '@/api/generated';
import { SortDirection } from '@/components/dataTable/model/DataTable.model.ts';
import { QuestionSortKeyEnum } from '@/components/questionBankList/model/QuestionSortKey.enum.ts';
import { ExaminerGradedType } from '@/models/question/QuestionTypePolicy.model.ts';
import { StatusActionEnum } from '@/models/status/StatusAction.enum.ts';
import { AttemptOutcomeEnum } from '@/views/attempt/model/AttemptOutcome.enum.ts';
import { AttemptDetailsTabEnum } from '@/views/attempts/model/AttemptDetailsTab.enum.ts';
import { AttemptResultFilterEnum } from '@/views/attempts/model/AttemptResultFilter.enum.ts';
import { GrantBlockEnum } from '@/views/attempts/model/GrantBlock.enum.ts';
import {
  DictionarySectionEnum,
  DictionaryStatusFilterEnum,
} from '@/views/dictionaryManagement/model/DictionaryManagement.enum.ts';
import { ImportDialogModeEnum } from '@/views/questionBank/model/ImportDialogMode.enum.ts';
import { MediaMessageKey } from '@/views/questionForm/model/MediaUploadIssue.model.ts';
import { NewVersionModeEnum } from '@/views/questionForm/model/NewVersionMode.enum.ts';
import { PositionScopeEnum } from '@/views/questionForm/model/PositionScope.enum.ts';
import { QuestionPageViewEnum } from '@/views/questionForm/model/QuestionPageView.enum.ts';
import { HistoryField } from '@/views/questionHistory/model/HistoryField.model.ts';
import { SurveyHistoryField } from '@/views/surveys/model/SurveyHistory.model.ts';
import { SurveyResponseStateEnum } from '@/views/surveys/model/SurveyResponseState.enum.ts';
import { TestHistoryField } from '@/views/tests/model/TestHistory.model.ts';
export type ViewsTranslation = {
  attempt: {
    ended: {
      description: string;
      statusLabel: string;
      title: string;
    };
    finish: {
      cancel: string;
      confirm: string;
      description: string;
      failed: string;
      open: string;
      title: string;
      unanswered_few?: string;
      unanswered_many?: string;
      unanswered_one: string;
      unanswered_other?: string;
      unansweredList: string;
    };
    header: {
      answered: string;
      noTimeLimit: string;
      questionOf: string;
      timeLeft: string;
      timeUp: string;
    };
    instruction: string;
    invalidated: {
      description: string;
      statusLabel: string;
      title: string;
    };
    locked: {
      backToTests: string;
      description: string;
      statusLabel: string;
      title: string;
    };
    navigation: {
      answerRequired: string;
      confirm: string;
      goTo: string;
      label: string;
      next: string;
      noGoingBack: string;
      previous: string;
    };
    result: {
      close: string;
      closeFailed: string;
      expired: string;
      mistakes: {
        noAnswer: string;
        questionNumber: string;
        title: string;
        withheld: string;
      };
      outcome: Record<AttemptOutcomeEnum, { label: string; title: string }>;
      percent: string;
      points: string;
      runner: {
        attemptsLeft_few?: string;
        attemptsLeft_many?: string;
        attemptsLeft_one: string;
        attemptsLeft_other?: string;
        retryAdvice: string;
        title: string;
      };
    };
    saveFailed: string;
    unavailable: {
      description: string;
      statusLabel: string;
      title: string;
    };
  };
  attempts: {
    columns: {
      attemptNo: string;
      duration: string;
      eventCount: string;
      person: string;
      result: string;
      startedAt: string;
      status: string;
      test: string;
    };
    description: string;
    details: {
      attemptNo: string;
      backToList: string;
      continueExam: string;
      fields: {
        allowGoingBack: string;
        closedAt: string;
        duration: string;
        eventCount: string;
        examiner: string;
        finishedAt: string;
        finishedBy: string;
        passThreshold: string;
        personPosition: string;
        sapNumber: string;
        startedAt: string;
        startedBy: string;
        testPosition: string;
        timeLimit: string;
      };
      forbidden: {
        action: string;
        description: string;
        title: string;
      };
      goingBack: {
        allowed: string;
        notAllowed: string;
      };
      invalidate: {
        alreadyInvalidated: string;
        confirm: string;
        description: string;
        descriptionRunning: string;
        failed: string;
        open: string;
        success: string;
        title: string;
      };
      invalidated: {
        byOnly: string;
        reason: string;
        title: string;
        unknownPerson: string;
        when: string;
      };
      notFound: {
        action: string;
        description: string;
        title: string;
      };
      noThreshold: string;
      noTimeLimit: string;
      notYet: string;
      questions: {
        answeredAt: string;
        empty: string;
        exam: {
          answerKey: string;
          comment: string;
          failed: string;
          grade: string;
          gradedAt: string;
          gradedBy: string;
          noComment: string;
          notGraded: string;
          passed: string;
          points: string;
          score: string;
          topicFallback: string;
          topicNumber: string;
          topics: string;
        };
        noAnswer: string;
        noGrade: string;
        pointsPending: string;
        questionNumber: string;
      };
      summaryLabel: string;
      tabs: Record<AttemptDetailsTabEnum, string>;
      tabsLabel: string;
      threshold: string;
      timeLimit: string;
      timeline: {
        by: string;
        empty: string;
        events: Record<AttemptEventTypeDto, string>;
        forbidden: {
          description: string;
          title: string;
        };
        kinds: Record<AttemptTimelineKindDto, string>;
        label: string;
        reason: string;
      };
    };
    empty: {
      action: string;
      description: string;
      title: string;
    };
    examMarker: string;
    filters: {
      allModes: string;
      allResults: string;
      allStatuses: string;
      allTests: string;
      mode: string;
      result: string;
      sapNumber: string;
      sapNumberPlaceholder: string;
      startedFrom: string;
      startedTo: string;
      status: string;
      test: string;
    };
    mode: Record<AttemptModeDto, string>;
    outcome: Record<AttemptOutcomeEnum, string>;
    percent: string;
    person: {
      grant: {
        unavailable: Record<GrantBlockEnum, string>;
      } & AttemptPersonActionTranslation;
      lock: AttemptPersonActionTranslation;
      subject: string;
      subjectWithoutTest: string;
      title: string;
      unlock: AttemptPersonActionTranslation;
    };
    points: string;
    result: Record<AttemptResultFilterEnum, string>;
    status: Record<AttemptStatusDto, string>;
    tableLabel: string;
    title: string;
  };
  comingSoon: {
    description: string;
    goToDashboard: string;
    hint: string;
    statusLabel: string;
  };
  devPatterns: {
    description: string;
    form: {
      description: string;
      fields: {
        category: string;
        content: string;
        contentPlaceholder: string;
        email: string;
        emailPlaceholder: string;
      };
      reset: string;
      savedMessage: string;
      simulateServerError: string;
      submit: string;
      title: string;
    };
    notifications: {
      description: string;
      errorMessage: string;
      showError: string;
      showSuccess: string;
      successMessage: string;
      title: string;
    };
    states: {
      calloutBody: string;
      calloutTitle: string;
      description: string;
      emptyLabel: string;
      errorLabel: string;
      loadingLabel: string;
      title: string;
    };
    table: {
      columns: {
        category: string;
        code: string;
        content: string;
        status: string;
        updatedAt: string;
      };
      description: string;
      filters: {
        allCategories: string;
        allStatuses: string;
        category: string;
        search: string;
        status: string;
      };
      showEmptyDataset: string;
      title: string;
    };
    title: string;
  };
  dictionaries: {
    questionCategory: {
      COMMUNICATION: string;
      CUSTOMER_SERVICE: string;
      FOOD_SAFETY: string;
      OCCUPATIONAL_SAFETY: string;
    };
    questionPurpose: Record<QuestionPurposeDto, string>;
    questionSource: Record<SourceTypeDto, string>;
    questionStatus: {
      ACTIVE: string;
      ARCHIVED: string;
      DRAFT: string;
    };
    questionType: Record<QuestionTypeDto, string>;
    scoringRule: Record<ScoringRuleDto, ScoringRuleTranslation>;
    translationStatus: Record<TranslationStatusDto, string>;
    versionStatus: Record<VersionStatusDto, string>;
  };
  dictionaryManagement: {
    categories: {
      actions: {
        activate: string;
        deactivate: string;
        delete: string;
        deleteUnavailable: string;
        edit: string;
        menu: string;
        moveDown: string;
        moveUp: string;
      };
      activated: string;
      add: string;
      columns: {
        actions: string;
        name: string;
        order: string;
        prefix: string;
        questions: string;
        status: string;
      };
      created: string;
      deactivated: string;
      deleteDialog: {
        cancel: string;
        confirm: string;
        description: string;
        done: string;
        failed: string;
        inUse: string;
        title: string;
      };
      empty: {
        description: string;
        title: string;
      };
      form: {
        cancel: string;
        createTitle: string;
        editTitle: string;
        name: string;
        prefix: string;
        prefixHint: string;
        prefixLocked_few?: string;
        prefixLocked_many?: string;
        prefixLocked_one: string;
        prefixLocked_other?: string;
        save: string;
      };
      orderHint: string;
      prefixNotice: string;
      reorderFailed: string;
      saved: string;
      status: Record<DictionaryStatusFilterEnum, string>;
      statusDialog: {
        cancel: string;
        confirm: string;
        title: string;
        used_few?: string;
        used_many?: string;
        used_one: string;
        used_other?: string;
      };
      statusFailed: string;
      tableLabel: string;
    };
    description: string;
    positions: {
      columns: {
        code: string;
        company: string;
        inAnkieter: string;
        name: string;
        questions: string;
        status: string;
      };
      empty: {
        description: string;
        title: string;
      };
      noMatches: {
        description: string;
        title: string;
      };
      notice: string;
      noticeNeverRefreshed: string;
      search: string;
      status: Record<DictionaryStatusFilterEnum, string>;
      statusFilter: {
        all: string;
        label: string;
        options: Record<DictionaryStatusFilterEnum, string>;
      };
      tableLabel: string;
      usageFailed: string;
      usageToggle: string;
      usedOnly: string;
    };
    procedureNames: {
      columns: {
        name: string;
      };
      empty: {
        description: string;
        title: string;
      };
      notice: string;
      search: string;
      tableLabel: string;
    };
    questionCount_few?: string;
    questionCount_many?: string;
    questionCount_one: string;
    questionCount_other?: string;
    sections: Record<DictionarySectionEnum, string>;
    sectionsLabel: string;
    tags: {
      actions: {
        delete: string;
        deleteUnavailable: string;
        menu: string;
        merge: string;
        mergeUnavailable: string;
      };
      add: string;
      columns: {
        actions: string;
        label: string;
        questions: string;
        status: string;
      };
      created: string;
      deleteDialog: {
        cancel: string;
        confirm: string;
        description: string;
        done: string;
        failed: string;
        inUse: string;
        title: string;
      };
      empty: {
        description: string;
        title: string;
      };
      form: {
        cancel: string;
        label: string;
        save: string;
        similar: string;
        title: string;
      };
      merge: {
        cancel: string;
        confirm: string;
        noOptions: string;
        preview_few?: string;
        preview_many?: string;
        preview_one: string;
        preview_other?: string;
        source: string;
        target: string;
        targetPlaceholder: string;
        title: string;
        unavailable: string;
      };
      merged: string;
      mergeFailed: string;
      notice: string;
      search: string;
      status: Record<DictionaryStatusFilterEnum, string>;
      statusFilter: {
        all: string;
        label: string;
        options: Record<DictionaryStatusFilterEnum, string>;
      };
      tableLabel: string;
    };
    title: string;
  };
  exam: {
    attemptNo: string;
    backToList: string;
    closing: {
      progress: string;
      title: string;
    };
    details: string;
    empty: string;
    finish: {
      action: string;
      cancel: string;
      confirm: string;
      description: string;
      failed: string;
      gapDetails: string;
      gapQuestion: string;
      gapReasons: {
        comment: string;
        grade: string;
        unsaved: string;
      };
      gapsDescription: string;
      gapsTitle: string;
      refusedTitle: string;
      success: string;
      title: string;
    };
    finished: {
      description: string;
      title: string;
    };
    forbidden: {
      action: string;
      description: string;
      title: string;
    };
    grade: {
      commentMissing: string;
      failed: string;
      failedInline: string;
      gaps: {
        passed: string;
        points: string;
        score: string;
        topics_few?: string;
        topics_many?: string;
        topics_one: string;
        topics_other?: string;
      };
      over: string;
      retry: string;
      saved: string;
      saving: string;
    };
    invalidated: {
      description: string;
      title: string;
    };
    notFound: {
      action: string;
      description: string;
      title: string;
    };
    question: {
      notGraded: string;
      number: string;
      points: string;
    };
    summary: {
      finishedAt: string;
      finishedBy: string;
      label: string;
      noThreshold: string;
      notYet: string;
      passThreshold: string;
      personPosition: string;
      progress: string;
      progressValue: string;
      sapNumber: string;
      startedAt: string;
      startedBy: string;
      testPosition: string;
      threshold: string;
    };
  };
  questionBank: {
    actions: {
      duplicate: string;
      edit: string;
      history: string;
      menu: string;
      preview: string;
      retire: string;
      retireNoActiveVersion: string;
    };
    addQuestion: string;
    bulk: {
      back: string;
      cancel: string;
      change: {
        addTags: string;
        clearReview: string;
        markForReview: string;
        markForReviewWithReason: string;
        removeTags: string;
        retire: string;
        setCategory: string;
      };
      clear: string;
      done_few?: string;
      done_many?: string;
      done_one: string;
      done_other?: string;
      errors: {
        applyFailed: string;
        changedSincePreview: string;
      };
      next: string;
      operations: Record<BulkOperationDto, string>;
      params: {
        category: string;
        categoryPlaceholder: string;
        noTags: string;
        reason: string;
        reasonPlaceholder: string;
        tags: string;
      };
      preview: {
        affected_few?: string;
        affected_many?: string;
        affected_one: string;
        affected_other?: string;
        large: {
          description_few?: string;
          description_many?: string;
          description_one: string;
          description_other?: string;
          label: string;
          title: string;
        };
        more: string;
        nothingToChange: string;
        sample: string;
        skipped_few?: string;
        skipped_many?: string;
        skipped_one: string;
        skipped_other?: string;
      };
      selectAllMatching: string;
      selected_few?: string;
      selected_many?: string;
      selected_one: string;
      selected_other?: string;
      selectedAllMatching_few?: string;
      selectedAllMatching_many?: string;
      selectedAllMatching_one: string;
      selectedAllMatching_other?: string;
      selectPage: string;
      selectRow: string;
    };
    columns: {
      actions: string;
      category: string;
      key: string;
      languages: string;
      positions: string;
      status: string;
      summary: string;
      type: string;
      updatedAt: string;
      version: string;
    };
    description: string;
    draftInProgress: string;
    empty: {
      description: string;
      title: string;
    };
    exportAction: {
      empty: string;
      failed: string;
      hint_few?: string;
      hint_many?: string;
      hint_one: string;
      hint_other?: string;
      label: string;
    };
    filters: {
      activeSummary: string;
      all: string;
      author: string;
      category: string;
      changedFrom: string;
      changedTo: string;
      clearAll: string;
      hide: string;
      noPositions: string;
      noProcedures: string;
      noTags: string;
      optionsUnavailable: string;
      positionCode: string;
      positionPlaceholder: string;
      positionScope: string;
      positionScopeAll: string;
      positionScopeSelected: string;
      procedureName: string;
      procedurePlaceholder: string;
      purpose: string;
      review: string;
      reviewMarked: string;
      reviewUnmarked: string;
      search: string;
      show: string;
      showWithCount: string;
      source: string;
      status: string;
      tags: string;
      translationStatus: string;
      type: string;
    };
    hasMedia: string;
    importAction: string;
    importDialog: {
      ambiguousPositionCodes: string;
      cancel: string;
      checking: string;
      chooseAnother: string;
      close: string;
      confirm_few?: string;
      confirm_many?: string;
      confirm_one: string;
      confirm_other?: string;
      conversion: {
        categoriesUnavailable: string;
        category: string;
        categoryPlaceholder: string;
        check: string;
        checking: string;
        description: string;
        dropzone: string;
        fileChosen: string;
        issues: {
          category: string;
          checkFailed: string;
          convertFailed: string;
          unreadable: string;
        };
        limits: string;
        notes: string;
        report: {
          columns: {
            number: string;
            problem: string;
            row: string;
          };
          download: string;
          downloadAgain: string;
          empty: string;
          goToImport: string;
          listLabel: string;
          nextDescription: string;
          nextTitle: string;
          noNumber: string;
          problemCodes: Record<ConversionProblemCodeDto, string>;
          problems: string;
          problemsDescription: string;
          problemsTitle_few?: string;
          problemsTitle_many?: string;
          problemsTitle_one: string;
          problemsTitle_other?: string;
          questions: string;
          readyDescription: string;
          readyTitle: string;
        };
      };
      done_few?: string;
      done_many?: string;
      done_one: string;
      done_other?: string;
      errorCodes: Record<ImportErrorCodeDto, string>;
      issues: {
        changed: string;
        checkFailed: string;
        forbidden: string;
        importFailed: string;
        refused: string;
        tooLarge: string;
        tooManyRows_few?: string;
        tooManyRows_many?: string;
        tooManyRows_one: string;
        tooManyRows_other?: string;
        tooManyRowsNoCount: string;
        unreadable: string;
      };
      modes: Record<ImportDialogModeEnum, string>;
      modesLabel: string;
      pick: {
        description: string;
        dropzone: string;
        limits: string;
        notes: string;
      };
      report: {
        activated: string;
        columns: {
          column: string;
          problem: string;
          row: string;
          sheet: string;
        };
        drafts: string;
        empty: string;
        errorsDescription: string;
        errorsTitle_few?: string;
        errorsTitle_many?: string;
        errorsTitle_one: string;
        errorsTitle_other?: string;
        file: string;
        listLabel: string;
        readyDraftsOnly: string;
        readyTitle: string;
        readyWithActive: string;
        rows: string;
        toFix: string;
        wholeRow: string;
      };
      template: {
        description: string;
        download: string;
        failed: string;
        title: string;
      };
      title: string;
    };
    languageStatus: string;
    noSummary: string;
    positionsCell: {
      all: string;
      collapse: string;
      more_few?: string;
      more_many?: string;
      more_one: string;
      more_other?: string;
      outdated: string;
    };
    retireDialog: {
      confirm: string;
      description: string;
      done: string;
      failed: string;
      title: string;
    };
    review: string;
    reviewMarker: string;
    search: {
      label: string;
      language: string;
      noMatch: string;
      placeholder: string;
    };
    tableLabel: string;
    title: string;
  };
  questionFocus: {
    backToList: string;
    description: string;
    empty: {
      clearFilters: string;
      description: string;
      noMatchDescription: string;
      noMatchSearch: string;
      noMatchTitle: string;
      title: string;
    };
    navigation: {
      counter: string;
      edit: string;
      next: string;
      previous: string;
    };
    notFound: {
      description: string;
      title: string;
    };
    sort: {
      label: string;
      options: Record<QuestionSortKeyEnum, Record<SortDirection, string>>;
    };
    title: string;
  };
  questionForm: {
    actions: {
      fixTypo: string;
      fixTypoHint: string;
      newVersion: string;
      newVersionHint: string;
      saveDraft: string;
      saveDraftHint: string;
      status: {
        clean: string;
        cleanDescription: string;
        cleanDraftDescription: string;
        dirty: string;
        dirtyDescription: string;
        dirtyDraftDescription: string;
        locked: string;
        lockedDescription: string;
      };
    };
    answers: {
      add: string;
      answerLabel: string;
      answerPlaceholder: string;
      descriptionMultiple: string;
      descriptionOrdering: string;
      descriptionSingle: string;
      descriptionSurvey: string;
      markCorrect: string;
      moveDown: string;
      moveUp: string;
      position: string;
      remove: string;
      removeDisabled: string;
      title: string;
    };
    backToBank: string;
    backToFocus: string;
    classification: {
      addTag: string;
      businessKey: string;
      businessKeyAuto: string;
      businessKeyAutoNoCategory: string;
      businessKeyManual: string;
      businessKeyManualHelper: string;
      categoriesUnavailable: string;
      category: string;
      categoryPlaceholder: string;
      description: string;
      noPositions: string;
      noTags: string;
      outdatedNotice_few?: string;
      outdatedNotice_many?: string;
      outdatedNotice_one: string;
      outdatedNotice_other?: string;
      outdatedPosition: string;
      positions: string;
      positionScope: string;
      positionScopes: Record<PositionScopeEnum, { description: string; label: string }>;
      positionsLoading: string;
      positionsPlaceholder: string;
      positionsUnavailable: string;
      sources: {
        add: string;
        addDisabled: string;
        addLink: string;
        addLinkDisabled: string;
        description: string;
        descriptionPlaceholder: string;
        item: string;
        label: string;
        linkDescription: string;
        linkDescriptionPlaceholder: string;
        links: string;
        linkUrl: string;
        linkUrlPlaceholder: string;
        noLinks: string;
        openLink: string;
        procedureName: string;
        procedureNamePlaceholder: string;
        remove: string;
        removeDisabled: string;
        removeLink: string;
        type: string;
        typePlaceholder: string;
      };
      tagCreateError: string;
      tags: string;
      tagsPlaceholder: string;
      tagsUnavailable: string;
      title: string;
    };
    content: {
      body: string;
      bodyPlaceholder: string;
      description: string;
      explanation: string;
      explanationHelper: string;
      purpose: string;
      purposeHelper: string;
      purposeLocked: string;
      title: string;
      type: string;
      typeLocked: string;
    };
    create: {
      description: string;
      duplicateDescription: string;
      duplicateNotFound: {
        description: string;
        title: string;
      };
      title: string;
    };
    edit: {
      activateDialog: {
        confirm: string;
        description: string;
        title: string;
      };
      activateVersion: string;
      classificationDialog: {
        confirm: string;
        description: string;
        title: string;
      };
      draftBesideActive: {
        description: string;
        title: string;
      };
      history: string;
      noEditableVersion: {
        description: string;
        title: string;
      };
      notFound: {
        description: string;
        title: string;
      };
      title: string;
      versionLabel: string;
    };
    errors: {
      activateFailed: string;
      conflict: string;
      forbidden: string;
      generatedKeyTaken: string;
      keyTaken: string;
      notFound: string;
    };
    examinerKey: {
      answerKeyHelper: string;
      commentRequired: string;
      commentRequiredHelper: string;
      descriptions: Record<ExaminerGradedType, string>;
      examinerOnly: string;
      labels: Record<ExaminerGradedType, string>;
      placeholders: Record<ExaminerGradedType, string>;
      titles: Record<ExaminerGradedType, string>;
    };
    expectedAnswers: {
      add: string;
      addDisabled: string;
      answerLabel: string;
      answerPlaceholder: string;
      description: string;
      remove: string;
      removeDisabled: string;
      title: string;
    };
    formPreview: {
      description: string;
      hide: string;
      show: string;
      title: string;
    };
    media: {
      actions: {
        remove: string;
      };
      answerLabel: string;
      deleteDialog: {
        cancel: string;
        confirm: string;
        description: string;
        title: string;
      };
      description: string;
      disabled: {
        unsaved: string;
        unsavedAnswer: string;
      };
      dropzone: {
        answerLabel: string;
        formats: string;
        label: string;
        limits: string;
      };
      empty: string;
      errors: {
        FILE_TOO_LARGE: string;
        fileTooLargeWithoutActual: string;
        fileTooLargeWithoutLimit: string;
        forbidden: string;
        IMAGE_TOO_LARGE: string;
        notADraft: string;
        outOfLimits: string;
        removeFailed: string;
        unavailable: string;
        unknown: string;
        UNREADABLE: string;
        UNSUPPORTED_TYPE: string;
        unsupportedHeic: string;
        unsupportedQuickTime: string;
        VIDEO_CODEC_UNSUPPORTED: string;
        VIDEO_NOT_FASTSTART: string;
        VIDEO_RESOLUTION_TOO_HIGH: string;
        VIDEO_TOO_LONG: string;
      };
      kind: {
        IMAGE: string;
        VIDEO: string;
      };
      notifications: {
        removed: string;
        uploaded: string;
      };
      problems: { nextStep: string } & Record<MediaMessageKey, string>;
      readOnly: {
        description: string;
        title: string;
      };
      title: string;
      units: {
        dimensions: string;
        megabytes: string;
        minutes: string;
        minutesSeconds: string;
        seconds: string;
      };
      uploading: string;
    };
    newVersionDialog: {
      cancel: string;
      chooseDescription: string;
      confirm: string;
      description: string;
      modes: Record<NewVersionModeEnum, { description: string; label: string }>;
      title: string;
    };
    notifications: {
      activated: string;
      classificationSaved: string;
      created: string;
      typoFixed: string;
      versionCreated: string;
    };
    page: {
      title: string;
      views: Record<QuestionPageViewEnum, string>;
      viewsLabel: string;
    };
    practical: {
      add: string;
      addDisabled: string;
      description: string;
      remove: string;
      removeDisabled: string;
      scaleMax: string;
      scaleMaxHelper: string;
      title: string;
      topicLabel: string;
      topicPlaceholder: string;
      topicsToPick: string;
      topicsToPickHelper: string;
    };
    ruleErrors: Record<QuestionRuleDto, string>;
    scoring: {
      description: string;
      maxPoints: string;
      maxPointsHelper: string;
      notes: {
        examinerGraded: string;
        expectedAnswer: string;
        singleChoice: string;
      };
      scoringRule: string;
      title: string;
    };
    serverErrors: {
      answerBody: string;
      answerKey: string;
      answerPoints: string;
      answers: string;
      answersCorrect: string;
      answersOrder: string;
      body: string;
      businessKey: string;
      categoryId: string;
      expectedAnswers: string;
      maxPoints: string;
      positionCodes: string;
      procedureName: string;
      scaleMax: string;
      scoringRule: string;
      source: string;
      sourceDescription: string;
      sourceLinkDescription: string;
      sourceLinks: string;
      sourceLinkUrl: string;
      sources: string;
      tags: string;
      topics: string;
      topicsToPick: string;
    };
    translations: {
      answers: string;
      description: string;
      missing: string;
      sourceLanguage: string;
      title: string;
    };
    unsavedChanges: {
      description: string;
      leave: string;
      stay: string;
      title: string;
    };
  };
  questionHistory: {
    classification: {
      description: string;
      empty: string;
      title: string;
    };
    compare: {
      answerOrder: string;
      answerPoints: string;
      answers: string;
      body: string;
      expectedAnswers: string;
      explanation: string;
      from: string;
      maxPoints: string;
      needTwo: string;
      noChanges: string;
      scoringRule: string;
      to: string;
    };
    created: string;
    description: string;
    eventMeta: string;
    events: Record<HistoryEventKindDto, string>;
    fieldInLanguage: string;
    fields: Record<HistoryField, string>;
    notFound: {
      description: string;
      title: string;
    };
    otherField: string;
    panel: {
      compare: string;
      description: string;
      modeLabel: string;
      preview: string;
      title: string;
    };
    preview: {
      answers: string;
      body: string;
      correct: string;
      expectedAnswers: string;
      explanation: string;
      maxPoints: string;
      scoringRule: string;
    };
    readOnly: string;
    retired: string;
    title: string;
    values: {
      added: string;
      contentStatus: Record<TranslationStatusDto, string>;
      correct: string;
      empty: string;
      incorrect: string;
      item: string;
      no: string;
      removed: string;
      review: string;
      reviewWithoutReason: string;
      yes: string;
    };
    versionLabel: string;
    versions: {
      description: string;
      title: string;
    };
  };
  questionStatistics: {
    categories: {
      columns: {
        asked: string;
        averagePercent: string;
        category: string;
        correctPercent: string;
        questions: string;
      };
      description: string;
      emptyDescription: string;
      emptyTitle: string;
      tableLabel: string;
      title: string;
    };
    exportAction: {
      empty: string;
      failed: string;
      label: string;
    };
    filters: {
      allCategories: string;
      allTests: string;
      allTypes: string;
      anyCount: string;
      category: string;
      fromMonth: string;
      minAsked: string;
      minAskedOption_few?: string;
      minAskedOption_many?: string;
      minAskedOption_one: string;
      minAskedOption_other?: string;
      test: string;
      toMonth: string;
      type: string;
    };
    noValue: string;
    percent: string;
    question: {
      description: string;
      empty: {
        description: string;
        title: string;
      };
      noMatch: string;
      notFound: {
        description: string;
        title: string;
      };
      options: {
        chosen_few?: string;
        chosen_many?: string;
        chosen_one: string;
        chosen_other?: string;
        correct: string;
        title: string;
      };
      summary: string;
      version: string;
      versions: string;
    };
    ranking: {
      columns: {
        asked: string;
        averagePercent: string;
        category: string;
        correctPercent: string;
        question: string;
        type: string;
      };
      description: string;
      empty: {
        description: string;
        title: string;
      };
      noMatch: string;
      tableLabel: string;
      title: string;
    };
    tally: {
      asked: string;
      averagePercent: string;
      correct: string;
      correctPercent: string;
      partial: string;
      unanswered: string;
      wrong: string;
    };
  };
  settings: {
    check: {
      duration: {
        milliseconds: string;
        seconds: string;
      };
      forbidden: string;
      hint: string;
      label: string;
      notRunYet: string;
      pending: string;
      requestFailed: string;
      running: string;
      skippedHint: string;
      statuses: Record<CheckStatusDto, string>;
      steps: Record<CheckStepNameDto, string>;
      summary: {
        failed: string;
        ok: string;
      };
      technicalDetail: string;
    };
    description: string;
    details: {
      label: string;
      loadFailed: string;
      notSet: string;
    };
    jain: {
      checkAction: string;
      description: string;
      failures: Record<CheckFailureDto, string>;
      fields: {
        apiUrl: string;
        clientId: string;
        issuerUri: string;
      };
      notConfigured: {
        description: string;
        title: string;
      };
      title: string;
    };
    media: {
      checkAction: string;
      description: string;
      failures: Record<CheckFailureDto, string>;
      fields: {
        bucket: string;
        endpoint: string;
        publicEndpoint: string;
        publicEndpointSame: string;
        region: string;
      };
      notConfigured: {
        description: string;
        title: string;
      };
      title: string;
    };
    title: string;
  };
  surveyResponse: {
    actions: {
      answerRequired: string;
      interrupt: string;
      next: string;
      previous: string;
      refuse: string;
      submit: string;
    };
    anonymous: string;
    ended: {
      refused: { description: string; statusLabel: string; title: string };
      submitted: { description: string; statusLabel: string; title: string };
    };
    header: {
      answered: string;
      questionOf: string;
    };
    instruction: string;
    interrupt: {
      cancel: string;
      confirm: string;
      description: string;
      title: string;
    };
    optional: string;
    refuseFailed: string;
    saveFailed: string;
    submitFailed: string;
    unavailable: {
      description: string;
      statusLabel: string;
      title: string;
    };
  };
  surveys: {
    actions: {
      copy: string;
      delete: string;
      deleteOnlyDraft: string;
      edit: string;
      history: string;
      menu: string;
      preview: string;
      responses: string;
      start: string;
      startOnlyActive: string;
    };
    addSurvey: string;
    answers: {
      anonymous: string;
      named: string;
    };
    columns: {
      actions: string;
      answers: string;
      name: string;
      questions: string;
      status: string;
      updatedAt: string;
    };
    copied: string;
    copyFailed: string;
    copyForbidden: string;
    deleteDialog: {
      cancel: string;
      confirm: string;
      description: string;
      done: string;
      failed: string;
      forbidden: string;
      notDraft: string;
      title: string;
    };
    description: string;
    empty: {
      description: string;
      title: string;
    };
    filters: {
      allCategories: string;
      allStatuses: string;
      category: string;
      status: string;
    };
    form: {
      actions: {
        cancel: string;
        save: string;
        saveDraft: string;
      };
      activeNotice: {
        description: string;
        title: string;
      };
      areas: {
        description: string;
        empty: string;
        questions: CountTranslation;
        shareLabel: string;
        title: string;
      };
      backToList: string;
      basics: {
        anonymous: string;
        anonymousHelper: string;
        anonymousLocked: string;
        description: string;
        instructions: string;
        instructionsHelper: string;
        name: string;
        namePlaceholder: string;
        title: string;
      };
      create: {
        description: string;
        title: string;
      };
      edit: {
        description: string;
        notFound: {
          description: string;
          title: string;
        };
      };
      errors: {
        anonymityLocked: string;
        conflict: string;
        forbidden: string;
        gaps: string;
        nameTaken: string;
        notFound: string;
      };
      gaps: {
        description: string;
        noGaps: string;
        readyDraft: string;
        savedOnly: string;
        title: string;
      };
      notifications: {
        created: string;
        saved: string;
        savedActive: string;
      };
      questions: {
        description: string;
        empty: {
          description: string;
          title: string;
        };
        limit: string;
        listLabel: string;
        moveDown: string;
        moveUp: string;
        needsAttention: string;
        noResults: string;
        noSummary: string;
        remove: string;
        search: string;
        searchFailed: string;
        searchHint: string;
        searching: string;
        searchPlaceholder: string;
        title: string;
        typeBlocked: string;
      };
      questionSetDialog: {
        cancel: string;
        confirm: string;
        description: string;
        title: string;
      };
      serverErrors: {
        anonymous: string;
        instructions: string;
        name: string;
        questions: string;
      };
    };
    gaps: { unknownQuestion: string } & Record<SurveyGapCodeDto, string>;
    history: {
      backToSurvey: string;
      description: string;
      fields: Record<SurveyHistoryField, string>;
      notFound: {
        action: string;
        description: string;
        title: string;
      };
      open: string;
    };
    listWarning: {
      label: string;
      tooltip: string;
    };
    preview: {
      close: string;
      description: string;
      empty: {
        description: string;
        title: string;
      };
      error: {
        description: string;
        title: string;
      };
      notFound: {
        description: string;
        title: string;
      };
      open: string;
      position: string;
      positionPlaceholder: string;
      questionNumber: string;
      refused: string;
      saveFirst: string;
      title: string;
    };
    responses: {
      backToSurveys: string;
      columns: {
        endedAt: string;
        person: string;
        position: string;
        startedAt: string;
        startedBy: string;
        state: string;
      };
      details: {
        anonymity: { anonymous: string; named: string };
        answers: {
          anonymous: string;
          answeredAt: string;
          emptyInProgress: string;
          emptyRefused: string;
          noAnswer: string;
          questionNumber: string;
          title: string;
        };
        backToList: string;
        fields: {
          anonymity: string;
          endedAt: string;
          position: string;
          sapNumber: string;
          startedAt: string;
          startedBy: string;
        };
        forbidden: { action: string; description: string; title: string };
        notFound: { action: string; description: string; title: string };
        notYet: string;
        summaryLabel: string;
      };
      empty: { description: string; title: string };
      filters: {
        allStatuses: string;
        sapNumber: string;
        sapNumberPlaceholder: string;
        startedFrom: string;
        startedTo: string;
        status: string;
        statuses: Record<SurveyResponseStatusDto, string>;
      };
      state: Record<SurveyResponseStateEnum, string>;
      tableLabel: string;
      title: string;
    };
    search: {
      label: string;
    };
    startResponse: {
      cancel: string;
      confirm: string;
      description: string;
      failed: string;
      gapsTitle: string;
      name: string;
      notActive: string;
      notReady: string;
      position: string;
      positionPlaceholder: string;
      sapNumber: string;
      title: string;
    };
    status: Record<SurveyStatusDto, string>;
    statusDialog: {
      actions: Record<
        StatusActionEnum,
        { confirm: string; description: string; done: string; refusedTitle: string; title: string }
      >;
      cancel: string;
      close: string;
      failed: string;
      forbidden: string;
      nameTaken: string;
      openSurvey: string;
      refusedIntro: string;
    };
    tableLabel: string;
    title: string;
  };
  tests: {
    actions: {
      copy: string;
      delete: string;
      deleteOnlyDraft: string;
      edit: string;
      history: string;
      menu: string;
      runExam: string;
      runExamOnlyActive: string;
      sample: string;
      start: string;
      startOnlyActive: string;
    };
    addTest: string;
    columns: {
      actions: string;
      length: string;
      name: string;
      parts: string;
      purpose: string;
      status: string;
      updatedAt: string;
    };
    copied: string;
    copyFailed: string;
    deleteDialog: {
      cancel: string;
      confirm: string;
      description: string;
      done: string;
      failed: string;
      forbidden: string;
      notDraft: string;
      title: string;
    };
    description: string;
    empty: {
      description: string;
      title: string;
    };
    filters: {
      allCategories: string;
      allPurposes: string;
      allStatuses: string;
      category: string;
      purpose: string;
      status: string;
    };
    form: {
      actions: {
        cancel: string;
        save: string;
        saveDraft: string;
      };
      backToList: string;
      basics: {
        description: string;
        instructions: string;
        instructionsHelper: string;
        name: string;
        namePlaceholder: string;
        purpose: string;
        purposePlaceholder: string;
        title: string;
      };
      create: {
        description: string;
        title: string;
      };
      edit: {
        activeNotice: {
          description: string;
          title: string;
        };
        description: string;
        notFound: {
          description: string;
          title: string;
        };
      };
      errors: {
        forbidden: string;
        gaps: string;
        nameTaken: string;
        notFound: string;
      };
      notifications: {
        created: string;
        saved: string;
        savedActive: string;
      };
      parts: {
        add: string;
        addLimit: string;
        category: string;
        categoryPlaceholder: string;
        chosen: {
          blocked: string;
          empty: string;
          limit: string;
          noResults: string;
          noSummary: string;
          remove: string;
          search: string;
          searchFailed: string;
          searchHint: string;
          searching: string;
          searchPlaceholder: string;
        };
        description: string;
        empty: {
          description: string;
          title: string;
        };
        moveDown: string;
        moveUp: string;
        questionCount: string;
        remove: string;
        title: string;
      };
      pool: {
        available: string;
        description: string;
        length: string;
        needed: string;
        noQuestions: string;
        notReady: string;
        positions: {
          description: string;
          other: string;
          part: string;
          position: string;
          ready: string;
          title: string;
        };
        practicalOnlyHint: string;
        progressLabel: string;
        questions: CountTranslation;
        ready: string;
        shortBy: string;
        skipped: Record<PoolSkipReasonDto, CountTranslation>;
        skippedIntro: string;
        states: Record<
          'error' | 'forbidden' | 'incomplete' | 'noParts' | 'noPurpose' | 'rejected',
          { description: string; title: string }
        >;
        sufficient: string;
        title: string;
        warnings: Record<TestPoolWarningCodeDto, string>;
      };
      rules: {
        allowGoingBack: string;
        allowGoingBackHelper: string;
        attemptLimit: string;
        attemptLimits: {
          add: string;
          description: string;
          limit: string;
          position: string;
          positionPlaceholder: string;
          remove: string;
          title: string;
        };
        description: string;
        lockAfterPass: string;
        lockAfterPassHelper: string;
        mistakeReview: string;
        noLimitHelper: string;
        passThreshold: string;
        passThresholdHelper: string;
        retryAdvice: string;
        retryAdviceHelper: string;
        retryAdvicePlaceholder: string;
        timeLimit: string;
        title: string;
      };
      serverErrors: {
        attemptLimit: string;
        attemptLimitPosition: string;
        attemptLimits: string;
        instructions: string;
        mistakeReview: string;
        name: string;
        partCategory: string;
        partQuestionCount: string;
        partQuestions: string;
        parts: string;
        passThreshold: string;
        purpose: string;
        retryAdvice: string;
        timeLimitMinutes: string;
      };
    };
    history: {
      backToTest: string;
      description: string;
      fields: Record<TestHistoryField, string>;
      notFound: {
        action: string;
        description: string;
        title: string;
      };
      open: string;
      values: {
        minutes: string;
        percent: string;
      };
    };
    listWarning: {
      label: string;
      tooltip: string;
    };
    mistakeReview: Record<MistakeReviewDto, string>;
    partKind: Record<TestPartKindDto, { description: string; label: string }>;
    purpose: Record<TestPurposeDto, string>;
    sample: {
      close: string;
      description: string;
      emptyPart: string;
      error: { description: string; title: string };
      notFound: { description: string; title: string };
      open: string;
      part: string;
      position: string;
      positionPlaceholder: string;
      questionNumber: string;
      redraw: string;
      refused: string;
      saveFirst: string;
      showCorrect: string;
      title: string;
    };
    search: {
      label: string;
    };
    startAttempt: {
      alreadyPassed: string;
      blocked: {
        back: string;
        limit: {
          confirm: string;
          confirmExam: string;
          description: string;
          descriptionExam: string;
          failed: string;
          reason: string;
          reasonPlaceholder: string;
          title: string;
        };
        locked: {
          confirm: string;
          confirmExam: string;
          description: string;
          descriptionExam: string;
          failed: string;
          title: string;
        };
      };
      cancel: string;
      confirm: string;
      description: string;
      exam: {
        alreadyPassed: string;
        confirm: string;
        description: string;
        failed: string;
        notReady: string;
        title: string;
      };
      failed: string;
      name: string;
      notReady: string;
      position: string;
      positionPlaceholder: string;
      sapNumber: string;
      targetPosition: string;
      targetPositionHelper: string;
      targetPositionPlaceholder: string;
      title: string;
    };
    status: Record<TestStatusDto, string>;
    statusDialog: {
      actions: Record<
        StatusActionEnum,
        { confirm: string; description: string; done: string; refusedTitle: string; title: string }
      >;
      cancel: string;
      categoryNotInUseAnyPart: string;
      close: string;
      failed: string;
      forbidden: string;
      gaps: Record<TestGapCodeDto, string>;
      nameTaken: string;
      openTest: string;
      poolTooSmallAnyPart: string;
      refusedIntro: string;
    };
    tableLabel: string;
    title: string;
  };
};

type AttemptPersonActionTranslation = {
  confirm: string;
  description: string;
  failed: string;
  open: string;
  success: string;
  title: string;
};

type CountTranslation = {
  count_few?: string;
  count_many?: string;
  count_one: string;
  count_other?: string;
};

type ScoringRuleTranslation = {
  description: string;
  label: string;
};

export const viewsTranslation: ViewsTranslation = {
  attempt: {
    ended: {
      description: 'Your answers have been saved.',
      statusLabel: 'Test finished',
      title: 'The test is over',
    },
    finish: {
      cancel: 'Back to the test',
      confirm: 'Finish the test',
      description: 'After you finish, you cannot change your answers.',
      failed: 'Could not finish the test. Try again.',
      open: 'Finish the test',
      title: 'Finish the test?',
      unanswered_one: '{{count}} question has no answer yet. Answer it to finish the test.',
      unanswered_other: '{{count}} questions have no answer yet. Answer them to finish the test.',
      unansweredList: 'Questions without an answer: {{list}}.',
    },
    header: {
      answered: 'Answered: {{answered}} of {{total}}',
      noTimeLimit: 'No time limit',
      questionOf: 'Question {{number}} of {{total}}',
      timeLeft: 'Time left',
      timeUp: 'Time is up. The test is closing.',
    },
    instruction: 'Instructions',
    invalidated: {
      description:
        'The administrator interrupted this attempt. Your answers are no longer saved. Speak to the person who started the test.',
      statusLabel: 'Attempt interrupted',
      title: 'This attempt was interrupted',
    },
    locked: {
      backToTests: 'Back to the test list',
      description:
        'The result can no longer be opened in this window. Hand the computer back to the person running the test.',
      statusLabel: 'Screen locked',
      title: 'The test is closed',
    },
    navigation: {
      answerRequired: 'Answer the question to continue.',
      confirm: 'Confirm and continue',
      goTo: 'Go to question {{number}}',
      label: 'Questions',
      next: 'Next',
      noGoingBack:
        'In this test you cannot go back to earlier questions. Once confirmed, an answer cannot be changed.',
      previous: 'Previous',
    },
    result: {
      close: 'Finish and lock the screen',
      closeFailed: 'Could not lock the screen. Try again.',
      expired: 'The time for the test ran out. The result counts the answers saved until then.',
      mistakes: {
        noAnswer: 'No answer',
        questionNumber: 'Question {{number}}',
        title: 'Mistakes',
        withheld: 'The correct answer is not shown: this question comes back in the next attempt.',
      },
      outcome: {
        [AttemptOutcomeEnum.FAILED]: { label: 'Not passed', title: 'The test is not passed' },
        [AttemptOutcomeEnum.PASSED]: { label: 'Passed', title: 'The test is passed' },
        [AttemptOutcomeEnum.SCORED]: { label: 'Result', title: 'The test is over' },
      },
      percent: '{{percent}}%',
      points: '{{points}} of {{maxPoints}} points',
      runner: {
        attemptsLeft_one: '{{count}} attempt left',
        attemptsLeft_other: '{{count}} attempts left',
        retryAdvice: 'Suggested break before the next attempt',
        title: 'For the person running the test',
      },
    },
    saveFailed: 'Could not save the answer. Try again.',
    unavailable: {
      description:
        'The test opens only in the browser where it was started. Go back to that browser or ask the person running the test for help.',
      statusLabel: 'Test unavailable',
      title: 'This test cannot be opened here',
    },
  },
  attempts: {
    columns: {
      attemptNo: 'Attempt',
      duration: 'Time',
      eventCount: 'Events',
      person: 'Person',
      result: 'Result',
      startedAt: 'Started',
      status: 'Status',
      test: 'Test',
    },
    description: 'Attempts at tests and their results.',
    details: {
      attemptNo: 'Attempt {{number}}',
      backToList: 'Back to attempts',
      continueExam: 'Back to the exam',
      fields: {
        allowGoingBack: 'Going back to questions',
        closedAt: 'Result screen closed at',
        duration: 'Time taken',
        eventCount: 'Events',
        examiner: 'Examiner',
        finishedAt: 'Finished',
        finishedBy: 'Finished by',
        passThreshold: 'Pass threshold',
        personPosition: 'Position held',
        sapNumber: 'SAP number',
        startedAt: 'Started',
        startedBy: 'Started by',
        testPosition: 'Test for position',
        timeLimit: 'Time limit',
      },
      forbidden: {
        action: 'Back to attempts',
        description: 'Ask the administrator for access if you need this attempt.',
        title: 'You do not have access to this attempt',
      },
      goingBack: {
        allowed: 'Allowed',
        notAllowed: 'Not allowed',
      },
      invalidate: {
        alreadyInvalidated: 'This attempt is already invalidated.',
        confirm: 'Invalidate attempt',
        description:
          'The attempt will not count towards the limit or the pass. It stays in the history with the reason.',
        descriptionRunning:
          'The attempt is still running. It ends at once and the person sees that the administrator interrupted it. It will not count towards the limit or the pass.',
        failed: 'Could not invalidate the attempt. Try again.',
        open: 'Invalidate',
        success: 'Attempt invalidated.',
        title: 'Invalidate this attempt?',
      },
      invalidated: {
        byOnly: 'By: {{by}}.',
        reason: 'Reason: {{reason}}',
        title: 'Attempt invalidated',
        unknownPerson: 'unknown',
        when: 'Invalidated {{date}}. By: {{by}}.',
      },
      notFound: {
        action: 'Back to attempts',
        description: 'The link may be out of date. Find the attempt in the list.',
        title: 'This attempt was not found',
      },
      noThreshold: 'None',
      noTimeLimit: 'None',
      notYet: 'Not yet',
      questions: {
        answeredAt: 'Answered {{time}}',
        empty: 'This attempt has no questions.',
        exam: {
          answerKey: 'Key for the examiner',
          comment: 'Examiner comment',
          failed: 'Failed',
          grade: 'Grade',
          gradedAt: 'Graded',
          gradedBy: 'Examiner',
          noComment: 'No comment',
          notGraded: 'The examiner has not graded this question yet.',
          passed: 'Passed',
          points: '{{points}} of {{max}} pts',
          score: '{{score}} of {{max}}',
          topicFallback: 'Topic {{number}}',
          topicNumber: '{{number}}.',
          topics: 'Topics asked',
        },
        noAnswer: 'No answer',
        noGrade: 'Not graded',
        pointsPending: 'Not scored yet',
        questionNumber: 'Question {{number}}',
      },
      summaryLabel: 'Summary',
      tabs: {
        [AttemptDetailsTabEnum.QUESTIONS]: 'Questions',
        [AttemptDetailsTabEnum.TIMELINE]: 'Timeline',
      },
      tabsLabel: 'Attempt sections',
      threshold: '{{percent}}%',
      timeLimit: '{{count}} min',
      timeline: {
        by: 'By: {{name}}',
        empty: 'Nothing has been recorded for this attempt yet.',
        events: {
          [AttemptEventTypeDto.ContextMenu]: 'Context menu opened',
          [AttemptEventTypeDto.Copy]: 'Text copied',
          [AttemptEventTypeDto.FocusBack]: 'Came back to the test window',
          [AttemptEventTypeDto.FocusLost]: 'Left the test window',
          [AttemptEventTypeDto.FullscreenLeft]: 'Left full screen',
          [AttemptEventTypeDto.Paste]: 'Text pasted',
          [AttemptEventTypeDto.PrintScreen]: 'Print Screen key pressed',
          [AttemptEventTypeDto.QuestionOpened]: 'Question opened',
          [AttemptEventTypeDto.TabHidden]: 'Left the test tab',
          [AttemptEventTypeDto.TabVisible]: 'Came back to the test tab',
        },
        forbidden: {
          description: 'Ask the administrator for access if you need it.',
          title: 'You do not have access to this timeline',
        },
        kinds: {
          [AttemptTimelineKindDto.AnswerChanged]: 'Answer changed',
          [AttemptTimelineKindDto.Answered]: 'Answer given',
          [AttemptTimelineKindDto.Event]: 'Event',
          [AttemptTimelineKindDto.Expired]: 'Time ran out',
          [AttemptTimelineKindDto.Finished]: 'Attempt finished',
          [AttemptTimelineKindDto.GradeChanged]: 'Grade changed',
          [AttemptTimelineKindDto.Graded]: 'Graded',
          [AttemptTimelineKindDto.Invalidated]: 'Attempt invalidated',
          [AttemptTimelineKindDto.ResultClosed]: 'Result screen closed',
          [AttemptTimelineKindDto.Started]: 'Attempt started',
        },
        label: 'Attempt timeline',
        reason: 'Reason: {{reason}}',
      },
    },
    empty: {
      action: 'Go to tests',
      description: 'An attempt appears here once someone starts a test from the test list.',
      title: 'No attempts yet',
    },
    examMarker: 'Exam',
    filters: {
      allModes: 'Tests and exams',
      allResults: 'All results',
      allStatuses: 'All statuses',
      allTests: 'All tests',
      mode: 'Kind',
      result: 'Result',
      sapNumber: 'SAP number',
      sapNumberPlaceholder: 'Full SAP number',
      startedFrom: 'Started from',
      startedTo: 'Started to',
      status: 'Status',
      test: 'Test',
    },
    mode: {
      [AttemptModeDto.Examined]: 'Exams',
      [AttemptModeDto.Self]: 'Tests',
    },
    outcome: {
      [AttemptOutcomeEnum.FAILED]: 'Failed',
      [AttemptOutcomeEnum.PASSED]: 'Passed',
      [AttemptOutcomeEnum.SCORED]: 'Scored',
    },
    percent: '{{percent}}%',
    person: {
      grant: {
        confirm: 'Grant attempt',
        description:
          'The person will be able to start the test once more, above the limit. The reason is kept in the history.',
        failed: 'Could not grant the attempt. Try again.',
        open: 'Grant an extra attempt',
        success: 'Extra attempt granted.',
        title: 'Grant an extra attempt?',
        unavailable: {
          [GrantBlockEnum.LOCKED_AFTER_PASS]:
            'The person has passed this test, and it takes no attempt after a pass.',
          [GrantBlockEnum.NO_LIMIT]: 'This test has no attempt limit, so nothing to grant.',
        },
      },
      lock: {
        confirm: 'Lock',
        description: 'The person cannot start this test until you unlock it.',
        failed: 'Could not lock the attempts. Try again.',
        open: 'Lock',
        success: "This person's attempts are locked.",
        title: "Lock this person's attempts?",
      },
      subject: 'SAP number {{sapNumber}} · {{test}}',
      subjectWithoutTest: 'SAP number {{sapNumber}}',
      title: 'Standing in the test',
      unlock: {
        confirm: 'Unlock',
        description: 'The person will be able to start this test again.',
        failed: 'Could not unlock the attempts. Try again.',
        open: 'Unlock',
        success: "This person's attempts are unlocked.",
        title: "Unlock this person's attempts?",
      },
    },
    points: '{{points}} of {{maxPoints}} pts',
    result: {
      [AttemptResultFilterEnum.FAILED]: 'Failed',
      [AttemptResultFilterEnum.PASSED]: 'Passed',
    },
    status: {
      [AttemptStatusDto.Expired]: 'Time ran out',
      [AttemptStatusDto.Finished]: 'Finished',
      [AttemptStatusDto.InProgress]: 'In progress',
      [AttemptStatusDto.Invalidated]: 'Invalidated',
    },
    tableLabel: 'Attempts at tests',
    title: 'Attempts',
  },
  comingSoon: {
    description: 'This module is being built. It will appear here in one of the next releases.',
    goToDashboard: 'Go to dashboard',
    hint: 'Other modules are available in the menu on the left.',
    statusLabel: 'Coming soon',
  },
  devPatterns: {
    description:
      'A screen for the team. It shows the shared components on local sample data, without a server connection.',
    form: {
      description:
        'Validation runs in the browser first. Errors returned by the server are shown next to the matching fields.',
      fields: {
        category: 'Category',
        content: 'Question content',
        contentPlaceholder: 'For example: How should chilled goods be stored?',
        email: 'Author email',
        emailPlaceholder: 'name@company.com',
      },
      reset: 'Clear form',
      savedMessage: 'Question saved.',
      simulateServerError: 'Simulate a rejection from the server',
      submit: 'Save question',
      title: 'Form',
    },
    notifications: {
      description: 'Short confirmations disappear on their own. Errors stay until closed.',
      errorMessage: 'Changes could not be saved. Try again.',
      showError: 'Show error',
      showSuccess: 'Show confirmation',
      successMessage: 'Changes saved.',
      title: 'Notifications',
    },
    states: {
      calloutBody: 'Use this block for information that helps, but does not block the work.',
      calloutTitle: 'Info notice',
      description:
        'Every list has three states besides the data itself. Each has its own component.',
      emptyLabel: 'Empty',
      errorLabel: 'Error',
      loadingLabel: 'Loading',
      title: 'Screen states',
    },
    table: {
      columns: {
        category: 'Category',
        code: 'Code',
        content: 'Content',
        status: 'Status',
        updatedAt: 'Updated',
      },
      description:
        'Paging, sorting and filtering are passed to the data source as one query, and kept in the address so the view can be shared.',
      filters: {
        allCategories: 'All categories',
        allStatuses: 'All statuses',
        category: 'Category',
        search: 'Search questions',
        status: 'Status',
      },
      showEmptyDataset: 'Show an empty list',
      title: 'Table',
    },
    title: 'UI patterns',
  },
  dictionaries: {
    questionCategory: {
      COMMUNICATION: 'Communication',
      CUSTOMER_SERVICE: 'Customer service',
      FOOD_SAFETY: 'Food safety',
      OCCUPATIONAL_SAFETY: 'Occupational safety',
    },
    questionPurpose: {
      [QuestionPurposeDto.Survey]: 'Survey',
      [QuestionPurposeDto.Test]: 'Test',
    },
    questionSource: {
      [SourceTypeDto.Handbook]: 'Handbook',
      [SourceTypeDto.Opl]: 'OPL',
      [SourceTypeDto.Other]: 'Other',
      [SourceTypeDto.Procedure]: 'Procedure',
    },
    questionStatus: {
      ACTIVE: 'Active',
      ARCHIVED: 'Archived',
      DRAFT: 'Draft',
    },
    questionType: {
      [QuestionTypeDto.ExpectedAnswer]: 'Expected answer',
      [QuestionTypeDto.MultipleChoice]: 'Multiple choice',
      [QuestionTypeDto.OpenText]: 'Open question',
      [QuestionTypeDto.Ordering]: 'Put in order',
      [QuestionTypeDto.PassFail]: 'Pass or fail',
      [QuestionTypeDto.Practical]: 'Practical task',
      [QuestionTypeDto.SingleChoice]: 'Single choice',
    },
    scoringRule: {
      [ScoringRuleDto.AllOrNothing]: {
        description: 'Points only for every correct answer ticked and no wrong one.',
        label: 'All or nothing',
      },
      [ScoringRuleDto.Partial]: {
        description:
          'Points in proportion to the correct answers ticked. Wrong ticks cost nothing, so ticking everything scores full points.',
        label: 'Partial',
      },
      [ScoringRuleDto.PartialWithPenalty]: {
        description: 'Each wrong tick takes points away. The question never scores below zero.',
        label: 'Partial with penalty',
      },
    },
    translationStatus: {
      [TranslationStatusDto.Approved]: 'Approved',
      [TranslationStatusDto.Draft]: 'Draft',
      [TranslationStatusDto.Missing]: 'Missing',
    },
    versionStatus: {
      [VersionStatusDto.Active]: 'Active',
      [VersionStatusDto.Draft]: 'Draft',
      [VersionStatusDto.Retired]: 'Retired',
    },
  },
  dictionaryManagement: {
    categories: {
      actions: {
        activate: 'Turn on',
        deactivate: 'Turn off',
        delete: 'Delete',
        deleteUnavailable: 'This category has questions, so it cannot be deleted.',
        edit: 'Edit',
        menu: 'Actions for category {{name}}',
        moveDown: 'Move category {{name}} down',
        moveUp: 'Move category {{name}} up',
      },
      activated: 'Category {{name}} is turned on.',
      add: 'Add category',
      columns: {
        actions: 'Actions',
        name: 'Name',
        order: 'Order',
        prefix: 'Prefix',
        questions: 'Questions',
        status: 'Status',
      },
      created: 'Category added.',
      deactivated: 'Category {{name}} is turned off.',
      deleteDialog: {
        cancel: 'Cancel',
        confirm: 'Delete',
        description: 'The category disappears from the dictionary. This cannot be undone.',
        done: 'Category {{name}} is deleted.',
        failed: 'The category could not be deleted. Try again.',
        inUse: 'The category is in use. Turn it off instead of deleting it.',
        title: 'Delete category {{name}}?',
      },
      empty: {
        description: 'Add the first category so authors can choose it for a question.',
        title: 'No categories yet',
      },
      form: {
        cancel: 'Cancel',
        createTitle: 'New category',
        editTitle: 'Edit category',
        name: 'Name',
        prefix: 'Prefix',
        prefixHint: 'For example BHP. It cannot be changed once the category has a question.',
        prefixLocked_one:
          'The prefix cannot be changed because the category already has {{count}} question.',
        prefixLocked_other:
          'The prefix cannot be changed because the category already has {{count}} questions.',
        save: 'Save',
      },
      orderHint: 'Authors see categories in this order in the question form.',
      prefixNotice:
        'Question keys are built from the prefix, for example BHP-1. Once a category has questions, its prefix cannot be changed.',
      reorderFailed: 'The order could not be changed. Try again.',
      saved: 'Changes saved.',
      status: {
        [DictionaryStatusFilterEnum.ACTIVE]: 'Active',
        [DictionaryStatusFilterEnum.INACTIVE]: 'Turned off',
      },
      statusDialog: {
        cancel: 'Cancel',
        confirm: 'Turn off',
        title: 'Turn off category {{name}}?',
        used_one:
          '{{count}} question uses it and keeps it, but new questions cannot be assigned to it. You can turn it on again later.',
        used_other:
          '{{count}} questions use it and keep it, but new questions cannot be assigned to it. You can turn it on again later.',
      },
      statusFailed: 'The category status could not be changed. Try again.',
      tableLabel: 'Question categories',
    },
    description:
      'Categories, tags, procedure names and positions that authors use to describe questions.',
    positions: {
      columns: {
        code: 'Number',
        company: 'Company',
        inAnkieter: 'Used in Ankieter',
        name: 'Position',
        questions: 'Questions',
        status: 'Status',
      },
      empty: {
        description: 'Positions appear here after the first nightly copy from jAIn.',
        title: 'No positions yet',
      },
      noMatches: {
        description: 'Change the search text or the filters to see more positions.',
        title: 'No position matches',
      },
      notice:
        'Positions come from jAIn and are copied every night. Here you can only mark which ones Ankieter uses. Lists for choosing a position offer only active positions used in Ankieter. Questions and tests keep the positions they already have. Last copied: {{date}}.',
      noticeNeverRefreshed:
        'Positions come from jAIn and are copied every night. Here you can only mark which ones Ankieter uses. Lists for choosing a position offer only active positions used in Ankieter. Questions and tests keep the positions they already have. They have not been copied yet.',
      search: 'Search by name or number',
      status: {
        [DictionaryStatusFilterEnum.ACTIVE]: 'Active',
        [DictionaryStatusFilterEnum.INACTIVE]: 'Switched off',
      },
      statusFilter: {
        all: 'All',
        label: 'Status',
        options: {
          [DictionaryStatusFilterEnum.ACTIVE]: 'Active',
          [DictionaryStatusFilterEnum.INACTIVE]: 'Switched off',
        },
      },
      tableLabel: 'Positions',
      usageFailed: 'Could not save the change for {{label}}. Try again.',
      usageToggle: 'Used in Ankieter: {{label}}',
      usedOnly: 'Used in Ankieter only',
    },
    procedureNames: {
      columns: {
        name: 'Procedure name',
      },
      empty: {
        description: 'A name appears here once an author enters it as the source of a question.',
        title: 'No procedure names yet',
      },
      notice:
        'Procedure names entered in questions, most used first. The question form suggests them while typing, so the same procedure always has the same name. Click a name to see the questions that cite it.',
      search: 'Search procedure names',
      tableLabel: 'Procedure names',
    },
    questionCount_one: '{{count}} question',
    questionCount_other: '{{count}} questions',
    sections: {
      [DictionarySectionEnum.CATEGORIES]: 'Categories',
      [DictionarySectionEnum.POSITIONS]: 'Positions',
      [DictionarySectionEnum.PROCEDURE_NAMES]: 'Procedure names',
      [DictionarySectionEnum.TAGS]: 'Tags',
    },
    sectionsLabel: 'Dictionary',
    tags: {
      actions: {
        delete: 'Delete',
        deleteUnavailable: 'This tag has questions, so it cannot be deleted.',
        menu: 'Actions for tag {{label}}',
        merge: 'Merge into another tag',
        mergeUnavailable: 'A turned off tag cannot be merged',
      },
      add: 'Add tag',
      columns: {
        actions: 'Actions',
        label: 'Tag',
        questions: 'Questions',
        status: 'Status',
      },
      created: 'Tag added.',
      deleteDialog: {
        cancel: 'Cancel',
        confirm: 'Delete',
        description: 'The tag disappears from the dictionary. This cannot be undone.',
        done: 'Tag “{{label}}” is deleted.',
        failed: 'The tag could not be deleted. Try again.',
        inUse: 'The tag is in use. Merge it into another tag instead.',
        title: 'Delete tag “{{label}}”?',
      },
      empty: {
        description: 'Authors add tags while writing questions. You can also add one here.',
        title: 'No tags yet',
      },
      form: {
        cancel: 'Cancel',
        label: 'Tag name',
        save: 'Add',
        similar: 'Similar tags that already exist',
        title: 'New tag',
      },
      merge: {
        cancel: 'Cancel',
        confirm: 'Merge tags',
        noOptions: 'No matching tags',
        preview_one:
          '{{count}} question moves from tag “{{source}}” to “{{target}}”. Tag “{{source}}” will be turned off.',
        preview_other:
          '{{count}} questions move from tag “{{source}}” to “{{target}}”. Tag “{{source}}” will be turned off.',
        source: 'Tag to merge',
        target: 'Target tag',
        targetPlaceholder: 'Choose the tag that stays',
        title: 'Merge tag “{{label}}”',
        unavailable: 'Could not load tags. Close the dialog and try again.',
      },
      merged: 'Tag “{{source}}” merged into “{{target}}”.',
      mergeFailed: 'The tags could not be merged. Try again.',
      notice:
        'Merge tags that mean the same thing, for example “bhp” and “b.h.p.”. Questions move to the tag you choose and the merged tag is turned off.',
      search: 'Search tags',
      status: {
        [DictionaryStatusFilterEnum.ACTIVE]: 'Active',
        [DictionaryStatusFilterEnum.INACTIVE]: 'Turned off',
      },
      statusFilter: {
        all: 'All statuses',
        label: 'Status',
        options: {
          [DictionaryStatusFilterEnum.ACTIVE]: 'Active',
          [DictionaryStatusFilterEnum.INACTIVE]: 'Turned off',
        },
      },
      tableLabel: 'Question tags',
    },
    title: 'Dictionaries',
  },
  exam: {
    attemptNo: 'Attempt {{number}}',
    backToList: 'Back to attempts',
    closing: {
      progress: '{{graded}} of {{total}} questions graded',
      title: 'When every question has a grade, finish the exam.',
    },
    details: 'Attempt details',
    empty: 'This exam has no questions.',
    finish: {
      action: 'Finish the exam',
      cancel: 'Back to the card',
      confirm: 'Finish the exam',
      description:
        'Once the exam is finished, the grades cannot be changed. The result is counted from the grades given.',
      failed: 'Could not finish the exam. Try again.',
      gapDetails: ' · {{category}}: {{reason}}',
      gapQuestion: 'Question {{number}}',
      gapReasons: {
        comment: 'the required comment is missing',
        grade: 'no grade yet',
        unsaved: 'the grade is not saved yet',
      },
      gapsDescription: 'Grade these questions and add the required comments, then finish the exam.',
      gapsTitle: 'Some questions are not graded',
      refusedTitle: 'The exam cannot be finished yet',
      success: 'Exam finished.',
      title: 'Finish the exam?',
    },
    finished: {
      description: 'The grades can no longer be changed. The result is in the summary.',
      title: 'This exam is finished',
    },
    forbidden: {
      action: 'Back to attempts',
      description: 'Ask the administrator for access if you need to run this exam.',
      title: 'You do not have access to this exam',
    },
    grade: {
      commentMissing: 'Add the required comment before you finish the exam.',
      failed: 'Could not save the grade of question {{number}}. Try again.',
      failedInline: 'Could not save the grade.',
      gaps: {
        passed: 'Choose passed or failed to save the grade.',
        points: 'Enter points within the range to save the grade.',
        score: 'Choose a score on the scale to save the grade.',
        topics_one: 'Tick exactly {{count}} topic to save the grade.',
        topics_other: 'Tick exactly {{count}} topics to save the grade.',
      },
      over: 'This exam is already finished or invalidated, so the grade cannot change.',
      retry: 'Try again',
      saved: 'Grade saved',
      saving: 'Saving the grade',
    },
    invalidated: {
      description: 'The grades cannot be changed and the result does not count.',
      title: 'This exam was invalidated',
    },
    notFound: {
      action: 'Back to attempts',
      description: 'The link may be out of date. Find the exam in the list of attempts.',
      title: 'This exam was not found',
    },
    question: {
      notGraded: 'Not graded',
      number: 'Question {{number}}',
      points: '{{points}} of {{maxPoints}} pts',
    },
    summary: {
      finishedAt: 'Finished',
      finishedBy: 'Finished by',
      label: 'Exam',
      noThreshold: 'None',
      notYet: 'Not yet',
      passThreshold: 'Pass threshold',
      personPosition: 'Position held',
      progress: 'Questions graded',
      progressValue: '{{graded}} of {{total}}',
      sapNumber: 'SAP number',
      startedAt: 'Started',
      startedBy: 'Examiner',
      testPosition: 'Exam for position',
      threshold: '{{percent}}%',
    },
  },
  questionBank: {
    actions: {
      duplicate: 'Duplicate',
      edit: 'Edit',
      history: 'Version history',
      menu: 'Actions for question {{key}}',
      preview: 'Preview as an employee',
      retire: 'Retire',
      retireNoActiveVersion: 'The question has no version in use.',
    },
    addQuestion: 'Add question',
    bulk: {
      back: 'Back',
      cancel: 'Cancel',
      change: {
        addTags: 'Tags to add: {{tags}}. The questions keep their current version.',
        clearReview: 'The review mark will be removed.',
        markForReview: 'The questions will be marked for review.',
        markForReviewWithReason: 'The questions will be marked for review. Reason: {{reason}}',
        removeTags: 'Tags to remove: {{tags}}. The questions keep their current version.',
        retire:
          'The versions in use will be retired and the questions will stop appearing in tests. Retired versions stay in the history.',
        setCategory: 'New category: {{category}}. The questions keep their current version.',
      },
      clear: 'Clear selection',
      done_one: '{{count}} question changed.',
      done_other: '{{count}} questions changed.',
      errors: {
        applyFailed: 'The change could not be made. Try again.',
        changedSincePreview:
          'The questions changed since the preview. Check the new numbers and confirm again.',
      },
      next: 'Next',
      operations: {
        [BulkOperationDto.AddTags]: 'Add tag',
        [BulkOperationDto.ClearReview]: 'Remove review mark',
        [BulkOperationDto.MarkForReview]: 'Mark for review',
        [BulkOperationDto.RemoveTags]: 'Remove tag',
        [BulkOperationDto.Retire]: 'Retire',
        [BulkOperationDto.SetCategory]: 'Change category',
      },
      params: {
        category: 'Category',
        categoryPlaceholder: 'Choose a category',
        noTags: 'No matching tags',
        reason: 'Reason (optional)',
        reasonPlaceholder: 'What should the reviewer look at',
        tags: 'Tags',
      },
      preview: {
        affected_one: '{{count}} question will change',
        affected_other: '{{count}} questions will change',
        large: {
          description_one:
            'This changes {{count}} question at once. Make sure the selection is the one you mean.',
          description_other:
            'This changes {{count}} questions at once. Make sure the selection is the one you mean.',
          label: 'Type {{count}} to confirm',
          title: 'Large change',
        },
        more: 'and {{count}} more',
        nothingToChange: 'Nothing will change. The selected questions are already in this state.',
        sample: 'Example questions',
        skipped_one: '{{count}} question stays as it is.',
        skipped_other: '{{count}} questions stay as they are.',
      },
      selectAllMatching: 'Select all {{count}} matching the filters',
      selected_one: '{{count}} question selected',
      selected_other: '{{count}} questions selected',
      selectedAllMatching_one: '{{count}} question matching the filters selected',
      selectedAllMatching_other: 'All {{count}} questions matching the filters selected',
      selectPage: 'Select questions on this page',
      selectRow: 'Select question {{key}}',
    },
    columns: {
      actions: 'Actions',
      category: 'Category',
      key: 'Key',
      languages: 'Languages',
      positions: 'Positions',
      status: 'Status',
      summary: 'Question',
      type: 'Type',
      updatedAt: 'Changed',
      version: 'Version',
    },
    description: 'Every question in one place. Narrow the list with filters or search the text.',
    draftInProgress: 'draft of version {{number}} in progress',
    empty: {
      description: 'Add the first question and it will appear here.',
      title: 'The question bank is empty',
    },
    exportAction: {
      empty: 'No question matches the filters.',
      failed: 'The file could not be downloaded. Try again.',
      hint_one: 'Download {{count}} question matching the filters',
      hint_other: 'Download {{count}} questions matching the filters',
      label: 'Export to Excel',
    },
    filters: {
      activeSummary: 'Narrowed by',
      all: 'All',
      author: 'Author',
      category: 'Category',
      changedFrom: 'Changed from',
      changedTo: 'Changed to',
      clearAll: 'Clear filters',
      hide: 'Hide filters',
      noPositions: 'No matching positions',
      noProcedures: 'No matching procedures',
      noTags: 'No matching tags',
      optionsUnavailable: 'Some filter options could not be loaded. Refresh the page.',
      positionCode: 'Position',
      positionPlaceholder: 'Name or number',
      positionScope: 'Meant for',
      positionScopeAll: 'All positions',
      positionScopeSelected: 'Selected positions',
      procedureName: 'Procedure',
      procedurePlaceholder: 'Procedure name',
      purpose: 'Purpose',
      review: 'Review',
      reviewMarked: 'Marked for review',
      reviewUnmarked: 'Not marked',
      search: 'Text or key',
      show: 'Filters',
      showWithCount: 'Filters ({{count}})',
      source: 'Source',
      status: 'Status',
      tags: 'Tags',
      translationStatus: 'Translation',
      type: 'Type',
    },
    hasMedia: 'Question with media',
    importAction: 'Import from Excel',
    importDialog: {
      ambiguousPositionCodes:
        'Several positions have this name: {{positions}}. Enter one of these numbers instead of the name.',
      cancel: 'Cancel',
      checking: 'Checking {{name}}',
      chooseAnother: 'Upload another file',
      close: 'Close',
      confirm_one: 'Import {{count}} question',
      confirm_other: 'Import {{count}} questions',
      conversion: {
        categoriesUnavailable: 'The categories could not be loaded. Refresh the page.',
        category: 'Category',
        categoryPlaceholder: 'Choose a category',
        check: 'Check',
        checking: 'Checking {{name}}',
        description:
          'Turn a question pool kept in the earlier layout into the import template. Every question goes into the chosen category. Nothing is saved in the question bank.',
        dropzone: 'Drag the pool file here or click to choose it',
        fileChosen: 'Chosen file: {{name}}',
        issues: {
          category: 'This category is no longer in use. Choose another one.',
          checkFailed: 'The file could not be checked. Try again.',
          convertFailed: 'The file in the template could not be prepared. Try again.',
          unreadable:
            'The file could not be read. Its first sheet needs the columns NR and Pytanie.',
        },
        limits: '.xlsx file, up to {{maxSize}} MB and {{maxRows}} questions',
        notes:
          'After the conversion every question is a test question worth 1 point and is imported as a draft. You can change this in the file before the import.',
        report: {
          columns: {
            number: 'Pool no.',
            problem: 'To fill in',
            row: 'Row',
          },
          download: 'Download in template',
          downloadAgain: 'Download again',
          empty:
            'The file has no questions. Check that its first sheet has the columns NR and Pytanie.',
          goToImport: 'Go to import',
          listLabel: 'What to fill in',
          nextDescription:
            'Fill in the downloaded file and upload it on the Import from template tab. The nr and uwagi columns are skipped by the import.',
          nextTitle: 'File downloaded',
          noNumber: 'None',
          problemCodes: {
            [ConversionProblemCodeDto.NoCorrectAnswer]:
              'No answer has a green background, so the correct one is unknown. Mark it in the file.',
            [ConversionProblemCodeDto.NoPosition]:
              'No position is marked for the question. Enter the positions in the file.',
            [ConversionProblemCodeDto.NoSource]: 'The source is empty. Enter it in the file.',
            [ConversionProblemCodeDto.ProcedureWithoutName]:
              'The source is a procedure, but without its name. Enter the procedure name in the file.',
            [ConversionProblemCodeDto.TooFewAnswers]:
              'The question has fewer than two answers. Add the missing answers in the file.',
            [ConversionProblemCodeDto.UnknownPosition]:
              'This position column does not match any active position. Enter the position in the file.',
            [ConversionProblemCodeDto.UnknownSource]:
              'This source is not on the list. Choose a source from the list in the file.',
            [ConversionProblemCodeDto.UnreadableRow]:
              'The row is neither a question with a number in NR nor an answer with a letter. It was skipped; check it in the pool file.',
          },
          problems: 'To fill in',
          problemsDescription:
            'Download the file in the template and fill in the places listed below. The same list is in the "Do poprawienia" sheet.',
          problemsTitle_one: '{{count}} place to fill in',
          problemsTitle_other: '{{count}} places to fill in',
          questions: 'Questions in file',
          readyDescription: 'Download the file in the template and upload it on the import tab.',
          readyTitle: 'Nothing left to fill in',
        },
      },
      done_one: '{{count}} question imported.',
      done_other: '{{count}} questions imported.',
      errorCodes: {
        [ImportErrorCodeDto.AmbiguousPosition]:
          'Several positions have this name. Enter the position number instead.',
        [ImportErrorCodeDto.InvalidValue]:
          'This column does not take this value. Check the column description in the template.',
        [ImportErrorCodeDto.KeyRepeated]: 'An earlier row in the file has the same key.',
        [ImportErrorCodeDto.KeyTaken]:
          'A question with this key is already in the bank. Change the key or leave it empty.',
        [ImportErrorCodeDto.Required]: 'The field is empty and the question needs it.',
        [ImportErrorCodeDto.Rule]: 'The question does not meet the rules of the question form.',
        [ImportErrorCodeDto.TooLong]: 'The text is too long. Shorten it.',
        [ImportErrorCodeDto.UnknownCategory]: 'There is no category in use with this name.',
        [ImportErrorCodeDto.UnknownKey]:
          'No question in the Pytania sheet has this key. Check the key in both sheets.',
        [ImportErrorCodeDto.UnknownPosition]:
          'There is no active position with this number or name. Check the Stanowiska sheet in the template.',
      },
      issues: {
        changed:
          'The question bank changed since the file was checked. The file is being checked again.',
        checkFailed: 'The file could not be checked. Try again.',
        forbidden: 'You do not have permission to import questions.',
        importFailed: 'The questions could not be imported. Try again.',
        refused: 'The file has errors. Fix them using the report and upload the file again.',
        tooLarge: 'The file is larger than {{maxSize}} MB. Split the questions into several files.',
        tooManyRows_one:
          'The file has {{count}} question and up to {{limit}} can be imported at once. Split the questions into several files.',
        tooManyRows_other:
          'The file has {{count}} questions and up to {{limit}} can be imported at once. Split the questions into several files.',
        tooManyRowsNoCount:
          'The file has more questions than can be imported at once ({{limit}}). Split the questions into several files.',
        unreadable:
          'The file could not be read. Use the template and keep the "Pytania" sheet and the column headers unchanged.',
      },
      modes: {
        [ImportDialogModeEnum.CONVERSION]: 'Conversion from the earlier layout',
        [ImportDialogModeEnum.IMPORT]: 'Import from template',
      },
      modesLabel: 'How to upload the questions',
      pick: {
        description:
          'Upload a file prepared on the template. Every row is checked first, and the questions are saved only after you confirm.',
        dropzone: 'Drag the file here or click to choose it',
        limits: '.xlsx file, up to {{maxSize}} MB and {{maxRows}} questions',
        notes:
          'Add pictures and films in the question editor after the import. A question whose key is already in the bank is not overwritten; change it in the form.',
      },
      report: {
        activated: 'Active at once',
        columns: {
          column: 'Column',
          problem: 'Problem',
          row: 'Row',
          sheet: 'Sheet',
        },
        drafts: 'As drafts',
        empty: 'The file has no questions. Fill in the "Pytania" sheet and upload the file again.',
        errorsDescription:
          'Questions are saved only when every row is correct. Fix the places listed below and upload the file again.',
        errorsTitle_one: 'The file has {{count}} error',
        errorsTitle_other: 'The file has {{count}} errors',
        file: 'File: {{name}}',
        listLabel: 'Errors in the file',
        readyDraftsOnly: 'The questions are saved as drafts once you confirm.',
        readyTitle: 'The file is ready to import',
        readyWithActive:
          'The questions are saved once you confirm. Active questions can appear in tests right away.',
        rows: 'Questions in file',
        toFix: 'To fix',
        wholeRow: 'Whole row',
      },
      template: {
        description: 'Column descriptions and example questions.',
        download: 'Download template',
        failed: 'The template could not be downloaded. Try again.',
        title: 'File template',
      },
      title: 'Import questions from Excel',
    },
    languageStatus: '{{language}}: {{status}}',
    noSummary: 'No text yet',
    positionsCell: {
      all: 'All positions',
      collapse: 'Show less',
      more_one: '+{{count}} position',
      more_other: '+{{count}} positions',
      outdated: 'Outdated',
    },
    retireDialog: {
      confirm: 'Retire',
      description:
        'The question will stop appearing in tests. The retired version stays in its history.',
      done: 'Question {{key}} was retired.',
      failed: 'The question could not be retired. Try again.',
      title: 'Retire question {{key}}?',
    },
    review: 'Review',
    reviewMarker: 'For review',
    search: {
      label: 'Search by question text or key',
      language: 'Language',
      noMatch: 'Search by question text or key, or clear the filters to see more questions.',
      placeholder: 'Search by text or key',
    },
    tableLabel: 'Questions',
    title: 'Question bank',
  },
  questionFocus: {
    backToList: 'Back to the list',
    description:
      'One question at a time, as an employee sees it. Use the arrow keys to move and Esc to go back to the list.',
    empty: {
      clearFilters: 'Clear filters',
      description: 'Add a question and you can review it here.',
      noMatchDescription: 'Change or clear the filters to see more questions.',
      noMatchSearch: 'Search by question text or key, or clear the filters to see more questions.',
      noMatchTitle: 'No question matches the filters',
      title: 'There are no questions to review',
    },
    navigation: {
      counter: '{{position}} of {{total}}',
      edit: 'Edit',
      next: 'Next',
      previous: 'Previous',
    },
    notFound: {
      description: 'The question may have been removed. Move to the next one.',
      title: 'This question is no longer available',
    },
    sort: {
      label: 'Order',
      options: {
        [QuestionSortKeyEnum.BUSINESS_KEY]: { asc: 'Key A to Z', desc: 'Key Z to A' },
        [QuestionSortKeyEnum.UPDATED_AT]: {
          asc: 'Changed earliest',
          desc: 'Changed most recently',
        },
        [QuestionSortKeyEnum.VERSION_NO]: { asc: 'Lowest version', desc: 'Highest version' },
      },
    },
    title: 'Question review',
  },
  questionForm: {
    actions: {
      fixTypo: 'Fix a typo',
      fixTypoHint: 'Saves the changes in the current version.',
      newVersion: 'New version',
      newVersionHint: 'Use it when the meaning of the question changes.',
      saveDraft: 'Save draft',
      saveDraftHint: 'Saves the changes in this draft.',
      status: {
        clean: 'No changes',
        cleanDescription: 'Edit the question above, then choose how to save it.',
        cleanDraftDescription: 'Edit the question above, then save the draft.',
        dirty: 'Unsaved changes',
        dirtyDescription:
          'A typo fix keeps the current version. A change in meaning needs a new version, and the current one is retired.',
        dirtyDraftDescription: 'Save the draft to keep your changes.',
        locked: 'This version is retired',
        lockedDescription: 'Your changes will be saved as a new version.',
      },
    },
    answers: {
      add: 'Add answer',
      answerLabel: 'Answer {{number}}',
      answerPlaceholder: 'Answer text',
      descriptionMultiple: 'Mark every correct answer.',
      descriptionOrdering:
        'Put the answers in the correct order by dragging them or using the arrows. The employee sees them in random order.',
      descriptionSingle: 'Mark the one correct answer.',
      descriptionSurvey: 'Survey questions have no correct answer.',
      markCorrect: 'Mark answer {{number}} as correct',
      moveDown: 'Move answer {{number}} down',
      moveUp: 'Move answer {{number}} up',
      position: 'Position {{number}} in the correct order',
      remove: 'Remove answer {{number}}',
      removeDisabled: 'A question needs at least two answers.',
      title: 'Answers',
    },
    backToBank: 'Back to question bank',
    backToFocus: 'Back to review',
    classification: {
      addTag: 'Add tag "{{label}}"',
      businessKey: 'Question key',
      businessKeyAuto: 'The key is issued automatically, for example {{example}}.',
      businessKeyAutoNoCategory: 'The key is issued automatically once a category is chosen.',
      businessKeyManual: 'Enter the key yourself',
      businessKeyManualHelper:
        'Only for questions carried over from an existing bank. At most {{max}} characters.',
      categoriesUnavailable: 'Categories could not be loaded. Refresh the page.',
      category: 'Category',
      categoryPlaceholder: 'Choose a category',
      description: 'Classification can change later without creating a new version.',
      noPositions: 'No matching positions',
      noTags: 'No matching tags',
      outdatedNotice_one:
        '{{count}} position is outdated: it has left jAIn or was switched off there. The question keeps it, but no other question can be given it. Remove it if the question should no longer reach it.',
      outdatedNotice_other:
        '{{count}} positions are outdated: they have left jAIn or were switched off there. The question keeps them, but no other question can be given them. Remove them if the question should no longer reach them.',
      outdatedPosition: '{{label}} (outdated)',
      positions: 'Positions',
      positionScope: 'Who the question is for',
      positionScopes: {
        [PositionScopeEnum.ALL]: {
          description: 'Any employee can get the question, whatever their position.',
          label: 'All positions',
        },
        [PositionScopeEnum.SELECTED]: {
          description: 'Only employees in the positions you choose get the question.',
          label: 'Selected positions',
        },
      },
      positionsLoading: 'Loading positions',
      positionsPlaceholder: 'Search by name or number',
      positionsUnavailable: 'Positions could not be loaded. Refresh the page.',
      sources: {
        add: 'Add source',
        addDisabled: 'A question can have at most {{max}} sources.',
        addLink: 'Add link',
        addLinkDisabled: 'A procedure can have at most {{max}} links.',
        description: 'Description',
        descriptionPlaceholder: 'For example: Chapter 3, storing goods',
        item: 'Source {{number}}',
        label: 'Sources',
        linkDescription: 'What the link leads to',
        linkDescriptionPlaceholder: 'For example: Procedure PDF',
        links: 'Links',
        linkUrl: 'Address',
        linkUrlPlaceholder: 'https://',
        noLinks: 'No links yet. Add a link to the procedure document or a training film.',
        openLink: 'Open link {{number}} in a new tab',
        procedureName: 'Procedure name',
        procedureNamePlaceholder: 'For example: Goods receiving procedure',
        remove: 'Remove source {{number}}',
        removeDisabled: 'A question needs at least one source.',
        removeLink: 'Remove link {{number}}',
        type: 'Source type',
        typePlaceholder: 'Choose a source type',
      },
      tagCreateError: 'The tag could not be added. Try again.',
      tags: 'Tags',
      tagsPlaceholder: 'Type to search',
      tagsUnavailable: 'Tags could not be loaded. Refresh the page.',
      title: 'Classification',
    },
    content: {
      body: 'Question',
      bodyPlaceholder: 'For example: How long can opened milk be kept in the cold store?',
      description: 'Choose how the question is answered and write its text.',
      explanation: 'Explanation after the test',
      explanationHelper: 'Optional. Shown after the test when the test is set to show it.',
      purpose: 'Purpose',
      purposeHelper: 'A test question goes only into tests, a survey question only into surveys.',
      purposeLocked: 'The purpose cannot change once the question exists.',
      title: 'Question',
      type: 'Question type',
      typeLocked: 'The type cannot change once the question exists.',
    },
    create: {
      description: 'Save a draft at any point and finish the question later.',
      duplicateDescription: 'Copy of question {{key}}. Save it as a new draft.',
      duplicateNotFound: {
        description:
          'The link may be out of date. Go back to the question bank and pick the question from there.',
        title: 'Question to copy not found',
      },
      title: 'New question',
    },
    edit: {
      activateDialog: {
        confirm: 'Activate',
        description:
          'Tests and surveys will use this version from now on. Earlier versions stay in the question history.',
        title: 'Activate version {{number}}?',
      },
      activateVersion: 'Activate version',
      classificationDialog: {
        confirm: 'Save',
        description:
          'You are changing the category or positions. They decide which tests and surveys show this question.',
        title: 'Save changes?',
      },
      draftBesideActive: {
        description: 'Tests keep using it until you activate this draft.',
        title: 'Version {{number}} stays in use',
      },
      history: 'Version history',
      noEditableVersion: {
        description:
          'Every version of this question is retired. Create a new version to change it.',
        title: 'No version to edit',
      },
      notFound: {
        description:
          'The link may be out of date. Go back to the question bank and open the question from there.',
        title: 'Question not found',
      },
      title: 'Edit question',
      versionLabel: 'Version {{number}}',
    },
    errors: {
      activateFailed: 'The version could not be activated. Try again.',
      conflict: 'The question changed in the meantime. Refresh the page and try again.',
      forbidden: 'You do not have permission to save this question.',
      generatedKeyTaken:
        'The key issued automatically is already taken. Enter the key yourself and save again.',
      keyTaken: 'This key is already taken. Enter a different one.',
      notFound: 'The question no longer exists. Go back to the question bank.',
    },
    examinerKey: {
      answerKeyHelper: 'The employee never sees this text.',
      commentRequired: 'The examiner has to add a comment',
      commentRequiredHelper:
        'When this is on, the examiner cannot save the result without a comment.',
      descriptions: {
        [QuestionTypeDto.OpenText]:
          'The employee writes the answer in their own words and the examiner grades it against this key.',
        [QuestionTypeDto.PassFail]:
          'The question has no answer options. The examiner decides whether the employee met the criterion.',
        [QuestionTypeDto.Practical]: 'Notes for the examiner on what to look for during the task.',
      },
      examinerOnly: 'Examiner only',
      labels: {
        [QuestionTypeDto.OpenText]: 'Answer key',
        [QuestionTypeDto.PassFail]: 'Criterion',
        [QuestionTypeDto.Practical]: 'Answer key',
      },
      placeholders: {
        [QuestionTypeDto.OpenText]:
          'For example: the answer gives the storage temperature and the use-by period.',
        [QuestionTypeDto.PassFail]:
          'For example: the employee puts on gloves and an apron before entering the hall.',
        [QuestionTypeDto.Practical]:
          'For example: check the order of the steps and the safety rules.',
      },
      titles: {
        [QuestionTypeDto.OpenText]: 'Answer key',
        [QuestionTypeDto.PassFail]: 'Pass criterion',
        [QuestionTypeDto.Practical]: 'Answer key',
      },
    },
    expectedAnswers: {
      add: 'Add expected answer',
      addDisabled: 'A question can have at most {{max}} expected answers.',
      answerLabel: 'Expected answer {{number}}',
      answerPlaceholder: 'For example: 4',
      description:
        'The employee types the answer and it is marked against this list. Letter case and extra spaces do not count; any other difference does, so "4" and "4,0" are different answers. If both are right, add both.',
      remove: 'Remove expected answer {{number}}',
      removeDisabled: 'A question needs at least one expected answer.',
      title: 'Expected answers',
    },
    formPreview: {
      description:
        'This is how an employee sees the question, including unsaved changes. Answers given here are not saved.',
      hide: 'Hide preview',
      show: 'Preview',
      title: 'Preview',
    },
    media: {
      actions: {
        remove: 'Remove {{name}}',
      },
      answerLabel: 'Media for answer {{number}}',
      deleteDialog: {
        cancel: 'Cancel',
        confirm: 'Remove',
        description: 'The media {{name}} will be removed from this version of the question.',
        title: 'Remove media?',
      },
      description: 'A photo or video the employee sees with the question.',
      disabled: {
        unsaved: 'You can add media after you save the question.',
        unsavedAnswer: 'You can add media after you save this answer.',
      },
      dropzone: {
        answerLabel: 'Drag media for this answer here or click to add it',
        formats: 'A JPEG, PNG or WebP photo, or an MP4 video',
        label: 'Drag a photo or video here or click to add it',
        limits: 'Photo up to 5 MB and 4000 px on the longer side, video up to 20 MB, 90 s and 720p',
      },
      empty: 'This question has no media yet.',
      errors: {
        FILE_TOO_LARGE:
          'The file is {{actual}} and the limit is {{limit}}. Make the file smaller and add it again.',
        fileTooLargeWithoutActual:
          'The file is larger than {{limit}}. Make the file smaller and add it again.',
        fileTooLargeWithoutLimit:
          'The file is too large. A photo can be up to 5 MB and a video up to 20 MB.',
        forbidden: 'You do not have permission to change media for this question.',
        IMAGE_TOO_LARGE:
          'The longer side of the photo is {{actual}} px and the limit is {{limit}} px. Resize the photo and add it again.',
        notADraft:
          'Media can be changed only in a draft. Create a new version and add the media there.',
        outOfLimits:
          'The file does not meet the requirements. Photos: JPEG, PNG or WebP up to 5 MB and 4000 px on the longer side. Videos: MP4 up to 20 MB, 90 seconds and 720p.',
        removeFailed: 'Could not remove the media. Try again.',
        unavailable: 'Media is not available right now. Try again in a moment.',
        unknown: 'Could not add the file. Try again.',
        UNREADABLE:
          'Could not read the file. It may be damaged or incomplete. Save it again and add it once more.',
        UNSUPPORTED_TYPE:
          'This file format is not supported. Add a JPEG, PNG or WebP photo, or an MP4 video.',
        unsupportedHeic:
          'HEIC photos from a phone are not supported. Save the photo as JPEG and add it again.',
        unsupportedQuickTime:
          'MOV videos are not supported. Save the video as MP4 and add it again.',
        VIDEO_CODEC_UNSUPPORTED:
          'Browsers cannot play this video. Save it as MP4 with H.264 video and AAC sound, then add it again.',
        VIDEO_NOT_FASTSTART:
          'The video has to download in full before it can play. Export it with the Fast start or Optimize for web option turned on, then add it again.',
        VIDEO_RESOLUTION_TOO_HIGH:
          'The video is {{actual}}p and the limit is {{limit}}p. Export it at a lower quality and add it again.',
        VIDEO_TOO_LONG:
          'The video runs {{actual}} and the limit is {{limit}}. Shorten the video and add it again.',
      },
      kind: {
        IMAGE: 'Photo',
        VIDEO: 'Video',
      },
      notifications: {
        removed: 'Media removed.',
        uploaded: 'Media added.',
      },
      problems: {
        fileTooLargeWithoutActual: 'The file is larger than {{limit}}.',
        fileTooLargeWithoutLimit:
          'The file is too large. A photo can be up to 5 MB and a video up to 20 MB.',
        [MediaRejectionReasonDto.FileTooLarge]:
          'The file is {{actual}} and the limit is {{limit}}.',
        [MediaRejectionReasonDto.ImageTooLarge]:
          'The longer side of the photo is {{actual}} px and the limit is {{limit}} px.',
        [MediaRejectionReasonDto.Unreadable]:
          'Could not read the file. It may be damaged or incomplete.',
        [MediaRejectionReasonDto.UnsupportedType]:
          'This file format is not supported. Save the photo as JPEG, PNG or WebP, or the video as MP4.',
        [MediaRejectionReasonDto.VideoCodecUnsupported]:
          'Browsers cannot play this video. Save it as MP4 with H.264 video and AAC sound.',
        [MediaRejectionReasonDto.VideoNotFaststart]:
          'The video has to download in full before it can play. Export it with the Fast start or Optimize for web option turned on.',
        [MediaRejectionReasonDto.VideoResolutionTooHigh]:
          'The video is {{actual}}p and the limit is {{limit}}p.',
        [MediaRejectionReasonDto.VideoTooLong]:
          'The video runs {{actual}} and the limit is {{limit}}.',
        nextStep: 'Fix the file and add it again.',
        outOfLimits:
          'The file does not meet the requirements. Photos: JPEG, PNG or WebP up to 5 MB and 4000 px on the longer side. Videos: MP4 up to 20 MB, 90 seconds and 720p.',
        unsupportedHeic: 'This is a HEIC photo. Save it as JPEG.',
        unsupportedQuickTime: 'This is a QuickTime (.mov) video. Save it as MP4.',
      },
      readOnly: {
        description:
          'Media changes only in a draft, so employees see the same thing as in the test. To change it, create a new version.',
        title: 'Change media in a new version',
      },
      title: 'Media',
      units: {
        dimensions: '{{width}} × {{height}} px',
        megabytes: '{{value}} MB',
        minutes: '{{minutes}} min',
        minutesSeconds: '{{minutes}} min {{seconds}} s',
        seconds: '{{seconds}} s',
      },
      uploading: 'Adding media {{name}}',
    },
    newVersionDialog: {
      cancel: 'Cancel',
      chooseDescription:
        'Choose what happens to the version in use. If you are only fixing a typo, choose Fix a typo instead.',
      confirm: 'Create new version',
      description:
        'The current version will be retired. If you are only fixing a typo, choose Fix a typo instead.',
      modes: {
        [NewVersionModeEnum.KEEP_CURRENT]: {
          description:
            'The question stays in tests with its current wording. The new version replaces it once you activate it.',
          label: 'The old version works until the new one is activated',
        },
        [NewVersionModeEnum.RETIRE_NOW]: {
          description:
            'For example when the procedure changed. The question leaves tests until you activate the new version.',
          label: 'Retire the old version now',
        },
      },
      title: 'Create a new version?',
    },
    notifications: {
      activated: 'The version is now active.',
      classificationSaved: 'Classification saved.',
      created: 'Draft saved.',
      typoFixed: 'Correction saved.',
      versionCreated: 'New version created.',
    },
    page: {
      title: 'Question {{key}}',
      views: {
        [QuestionPageViewEnum.EDIT]: 'Edit',
        [QuestionPageViewEnum.HISTORY]: 'Version history',
        [QuestionPageViewEnum.STATISTICS]: 'Statistics',
      },
      viewsLabel: 'Question view',
    },
    practical: {
      add: 'Add topic',
      addDisabled: 'A question can have at most {{max}} topics.',
      description:
        'The examiner picks some of these topics for the task and grades the result on the scale below.',
      remove: 'Remove topic {{number}}',
      removeDisabled: 'A practical question needs at least one topic.',
      scaleMax: 'Scale from 0 to',
      scaleMaxHelper: 'The highest grade the examiner can give, from {{min}} to {{max}}.',
      title: 'Topics',
      topicLabel: 'Topic {{number}}',
      topicPlaceholder: 'For example: Receiving a chilled delivery',
      topicsToPick: 'Topics the examiner picks',
      topicsToPickHelper: 'From {{min}} to {{max}}, at most as many as there are topics.',
    },
    ruleErrors: {
      [QuestionRuleDto.AnswerPointsPrecision]: 'Answer values can have at most one decimal place.',
      [QuestionRuleDto.AnswerPointsRange]: 'Answer values must be from 0 to 100.',
      [QuestionRuleDto.BodyMissing]: 'Enter the question text.',
      [QuestionRuleDto.BodyTooLong]: 'The question text can have at most 2,000 characters.',
      [QuestionRuleDto.CorrectOptionMissing]: 'Mark at least one correct answer.',
      [QuestionRuleDto.MaxPointsNotPositive]: 'Points must be greater than zero.',
      [QuestionRuleDto.MaxPointsPrecision]:
        'Points can have at most one decimal place, for example 2 or 2.5.',
      [QuestionRuleDto.MaxPointsTooHigh]: 'Points must be less than 10,000.',
      [QuestionRuleDto.OrderPlaceMissing]: 'Give every answer a place in the correct order.',
      [QuestionRuleDto.OrderPlacesInvalid]:
        'Places in the correct order must run from 1 to the number of answers, with no repeats.',
      [QuestionRuleDto.ScaleMaxMissing]: 'Enter the highest grade on the examiner scale.',
      [QuestionRuleDto.ScaleMaxRange]: 'The highest grade on the scale must be from 1 to 10.',
      [QuestionRuleDto.SingleChoiceManyCorrect]:
        'A single choice question has exactly one correct answer, and more are marked. Keep one or change the type to multiple choice.',
      [QuestionRuleDto.TopicsToPickMissing]: 'Enter how many topics the examiner picks.',
      [QuestionRuleDto.TopicsToPickRange]:
        'The examiner must pick at least one topic and no more than the question has.',
    },
    scoring: {
      description: 'How many points the question is worth and how they are counted.',
      maxPoints: 'Points',
      maxPointsHelper: 'At most one decimal place, for example 2.5.',
      notes: {
        examinerGraded: 'The examiner awards the points.',
        expectedAnswer:
          'The question scores full points when the answer matches any expected answer, and zero otherwise.',
        singleChoice:
          'A single choice question scores full points for the correct answer and zero otherwise.',
      },
      scoringRule: 'Scoring rule',
      title: 'Scoring',
    },
    serverErrors: {
      answerBody: 'Enter the answer text.',
      answerKey: 'Check the text for the examiner.',
      answerPoints: 'Answer values must be from 0 to 100, with at most one decimal place.',
      answers: 'Check the answers. A question needs at least two answers with text.',
      answersCorrect: 'Mark at least one correct answer.',
      answersOrder:
        'Check the order of the answers. A question needs at least two answers, each in its own position.',
      body: 'Enter the question text.',
      businessKey: 'The question key can have at most 40 characters. Shorten it.',
      categoryId: 'Choose a category.',
      expectedAnswers: 'Check the expected answers. Each needs text and can appear only once.',
      maxPoints:
        'Enter a number of points greater than zero and below 10,000, with at most one decimal place.',
      positionCodes: 'Check the job positions.',
      procedureName: 'Check the procedure name. The question can cite each procedure only once.',
      scaleMax: 'Enter a scale that goes up to at least 1.',
      scoringRule: 'Choose a scoring rule.',
      source: 'Check this source. Each source can appear only once.',
      sourceDescription: 'Check the source description.',
      sourceLinkDescription: 'Enter what the link leads to.',
      sourceLinks: 'Check the links. Each address can appear only once in a procedure.',
      sourceLinkUrl:
        'Enter a full address starting with http:// or https://. Each address can appear only once.',
      sources: 'Check the sources. A question needs at least one.',
      tags: 'Check the tags.',
      topics: 'Check the topics. Each needs text and can appear only once.',
      topicsToPick: 'The examiner cannot pick more topics than the list has.',
    },
    translations: {
      answers: 'Answers',
      description: 'Status of the question in each language.',
      missing: 'Not translated yet.',
      sourceLanguage: 'Source language. Edit its text in the Question section above.',
      title: 'Translations',
    },
    unsavedChanges: {
      description: 'Changes you have not saved will be lost.',
      leave: 'Leave without saving',
      stay: 'Stay',
      title: 'You have unsaved changes. Leave without saving?',
    },
  },
  questionHistory: {
    classification: {
      description:
        'Changes of category, sources, tags, positions and the review mark. They belong to the question, not to a version.',
      empty: 'The classification has not changed since the question was created.',
      title: 'Classification',
    },
    compare: {
      answerOrder: 'Place: {{value}}',
      answerPoints: 'Points: {{value}}',
      answers: 'Answers',
      body: 'Question',
      expectedAnswers: 'Expected answers',
      explanation: 'Explanation after the test',
      from: 'Earlier version',
      maxPoints: 'Points',
      needTwo: 'The question has only one version, so there is nothing to compare.',
      noChanges: 'These versions have the same text, answers and scoring.',
      scoringRule: 'Scoring rule',
      to: 'Later version',
    },
    created: 'Created {{date}} by {{author}}',
    description: 'Newest version first, each with its changes.',
    eventMeta: '{{date}}, {{author}}',
    events: {
      [HistoryEventKindDto.ClassificationChanged]: 'Classification changed',
      [HistoryEventKindDto.Corrected]: 'Corrected in place',
      [HistoryEventKindDto.Created]: 'Version created',
      [HistoryEventKindDto.MediaAdded]: 'Material added',
      [HistoryEventKindDto.MediaRemoved]: 'Material removed',
      [HistoryEventKindDto.ReviewChanged]: 'Review mark changed',
      [HistoryEventKindDto.StatusChanged]: 'Status changed',
    },
    fieldInLanguage: '{{field}} ({{language}})',
    fields: {
      allPositions: 'For all positions',
      answer: 'Answer',
      'answer.body': 'Answer text',
      'answer.correctOrder': 'Place in the correct order',
      'answer.displayOrder': 'Answer order',
      'answer.isCorrect': 'Correct answer',
      'answer.points': 'Points for the answer',
      answerKey: 'Examiner key',
      body: 'Question',
      category: 'Category',
      examinerCommentRequired: 'Examiner comment required',
      expectedAnswers: 'Expected answers',
      explanation: 'Explanation after the test',
      maxPoints: 'Points',
      media: 'Material',
      positionCodes: 'Job positions',
      review: 'Review mark',
      scaleMax: 'Top of the scale',
      scoringRule: 'Scoring rule',
      source: 'Source',
      sourceLocale: 'Source language',
      sourceName: 'Source name',
      sources: 'Sources',
      status: 'Status',
      tags: 'Tags',
      topics: 'Topics',
      topicsToPick: 'Topics to pick',
      translation: 'Language',
      translationStatus: 'Content status',
    },
    notFound: {
      description:
        'The link may be out of date. Go back to the question bank and open the question from there.',
      title: 'Question not found',
    },
    otherField: 'Other change',
    panel: {
      compare: 'Compare',
      description: 'The text exactly as it was while the version was in force.',
      modeLabel: 'Version view',
      preview: 'Preview',
      title: 'Version {{number}}',
    },
    preview: {
      answers: 'Answers',
      body: 'Question',
      correct: 'Correct',
      expectedAnswers: 'Expected answers',
      explanation: 'Explanation after the test',
      maxPoints: 'Points',
      scoringRule: 'Scoring rule',
    },
    readOnly:
      'The history cannot be changed. To go back to older text, create a new version in the question form.',
    retired: 'Retired {{date}}',
    title: 'Question history',
    values: {
      added: 'Added',
      contentStatus: {
        [TranslationStatusDto.Approved]: 'approved',
        [TranslationStatusDto.Draft]: 'draft',
        [TranslationStatusDto.Missing]: 'missing',
      },
      correct: 'correct',
      empty: 'none',
      incorrect: 'not correct',
      item: 'item',
      no: 'no',
      removed: 'Removed',
      review: '{{reason}} ({{person}})',
      reviewWithoutReason: 'Marked by {{person}}',
      yes: 'yes',
    },
    versionLabel: 'Version {{number}}',
    versions: {
      description: 'Choose a version to see its text.',
      title: 'Versions',
    },
  },
  questionStatistics: {
    categories: {
      columns: {
        asked: 'Times asked',
        averagePercent: 'Average score',
        category: 'Category',
        correctPercent: 'Correct',
        questions: 'Questions',
      },
      description:
        'Questions summed by category for the same period, test and type. The category and minimum count filters do not apply here.',
      emptyDescription: 'No question from any category was asked in this period.',
      emptyTitle: 'No categories to show',
      tableLabel: 'Question performance by category',
      title: 'By category',
    },
    exportAction: {
      empty: 'There is nothing to export for these filters.',
      failed: 'Could not export the report. Try again.',
      label: 'Export to Excel',
    },
    filters: {
      allCategories: 'All categories',
      allTests: 'All tests',
      allTypes: 'All types',
      anyCount: 'Any number of times',
      category: 'Category',
      fromMonth: 'From month',
      minAsked: 'Minimum times asked',
      minAskedOption_one: 'At least {{count}} time',
      minAskedOption_other: 'At least {{count}} times',
      test: 'Test',
      toMonth: 'To month',
      type: 'Question type',
    },
    noValue: '—',
    percent: '{{percent}}%',
    question: {
      description: 'How people answer this question in tests and exams.',
      empty: {
        description: 'The question has not been asked in any test or exam yet.',
        title: 'No answers yet',
      },
      noMatch: 'The question was not asked in this period or test. Change the filters.',
      notFound: {
        description: 'The question may have been deleted. Go back to the question bank.',
        title: 'Question not found',
      },
      options: {
        chosen_one: 'Chosen {{count}} time',
        chosen_other: 'Chosen {{count}} times',
        correct: 'Correct',
        title: 'Answers chosen',
      },
      summary: 'All versions',
      version: 'Version {{number}}',
      versions: 'By version',
    },
    ranking: {
      columns: {
        asked: 'Times asked',
        averagePercent: 'Average score',
        category: 'Category',
        correctPercent: 'Correct',
        question: 'Question',
        type: 'Type',
      },
      description: 'How questions fare in tests and exams, the hardest first.',
      empty: {
        description: 'No question has been asked in a finished test or exam yet.',
        title: 'No answers yet',
      },
      noMatch: 'No question matches these filters. Widen the period or clear the filters.',
      tableLabel: 'Question performance',
      title: 'Question performance',
    },
    tally: {
      asked: 'Times asked',
      averagePercent: 'Average score',
      correct: 'Correct',
      correctPercent: 'Correct answers',
      partial: 'Partly correct',
      unanswered: 'No answer',
      wrong: 'Wrong',
    },
  },
  settings: {
    check: {
      duration: {
        milliseconds: '{{value}} ms',
        seconds: '{{value}} s',
      },
      forbidden: 'You do not have permission to check connections.',
      hint: 'A check does not save anything, so you can run it as often as you need.',
      label: 'Check',
      notRunYet:
        'This connection has not been checked yet. The result will appear here, step by step.',
      pending: 'Checking. A step that gets no answer can take a few seconds.',
      requestFailed: 'The check could not be started. Try again.',
      running: 'Checking…',
      skippedHint: 'Skipped because an earlier step failed.',
      statuses: {
        [CheckStatusDto.Failed]: 'Failed',
        [CheckStatusDto.Passed]: 'Passed',
        [CheckStatusDto.Skipped]: 'Skipped',
      },
      steps: {
        [CheckStepNameDto.Configuration]: 'Settings',
        [CheckStepNameDto.Delete]: 'Removing the test file',
        [CheckStepNameDto.Jain]: 'Answer from jAIn',
        [CheckStepNameDto.Link]: 'Reading the file through a link',
        [CheckStepNameDto.Token]: 'Token from Keycloak',
        [CheckStepNameDto.Write]: 'Writing a test file',
      },
      summary: {
        failed: 'The connection does not work',
        ok: 'The connection works',
      },
      technicalDetail: 'Technical details',
    },
    description:
      'Connections from Ankieter to jAIn and to the media store. Each one can be checked step by step.',
    details: {
      label: 'Connection details',
      loadFailed: 'The connection details could not be loaded. Try again.',
      notSet: 'Not set',
    },
    jain: {
      checkAction: 'Check connection',
      description: 'The positions dictionary comes from jAIn.',
      failures: {
        [CheckFailureDto.BucketNotFound]:
          'jAIn answered in an unexpected way. The technical details will help find the cause.',
        [CheckFailureDto.ContentMismatch]:
          'jAIn answered in an unexpected way. The technical details will help find the cause.',
        [CheckFailureDto.IssuerNotFound]:
          'Keycloak has no such realm. Check the identity provider address.',
        [CheckFailureDto.LinkRefused]:
          'jAIn answered in an unexpected way. The technical details will help find the cause.',
        [CheckFailureDto.MissingRole]: 'The account does not have the ankieter-sync role.',
        [CheckFailureDto.NotAvailable]: 'jAIn does not offer data for Ankieter yet.',
        [CheckFailureDto.NotConfigured]:
          'Some settings the connection to jAIn needs are empty. Their names are in the technical details.',
        [CheckFailureDto.Refused]: 'jAIn refused access to this account.',
        [CheckFailureDto.RejectedCredentials]:
          'Keycloak did not accept the client credentials. Check the client ID and secret.',
        [CheckFailureDto.ServiceAccountDisabled]:
          'The client may not get tokens for itself. Turn on its service account in Keycloak.',
        [CheckFailureDto.TokenRejected]:
          'jAIn does not accept tokens from this Keycloak. Check that both use the same realm.',
        [CheckFailureDto.UnexpectedResponse]:
          'jAIn answered in an unexpected way. The technical details will help find the cause.',
        [CheckFailureDto.Unreachable]:
          'Nothing answers at this address. Check the address and whether the service is running.',
        [CheckFailureDto.WrongRegion]:
          'jAIn answered in an unexpected way. The technical details will help find the cause.',
      },
      fields: {
        apiUrl: 'jAIn address',
        clientId: 'Client ID',
        issuerUri: 'Identity provider (Keycloak)',
      },
      notConfigured: {
        description:
          'The jAIn address is missing, so the positions dictionary is not refreshed. Fill in the settings on the Ankieter server.',
        title: 'The connection to jAIn is not set up',
      },
      title: 'Connection to jAIn',
    },
    media: {
      checkAction: 'Check the store',
      description: 'The media store holds the pictures and films attached to questions.',
      failures: {
        [CheckFailureDto.BucketNotFound]: 'The store has no such bucket. Check its name.',
        [CheckFailureDto.ContentMismatch]:
          'The link returned different content than the file that was written. Check the address for browsers.',
        [CheckFailureDto.IssuerNotFound]:
          'The media store answered in an unexpected way. The technical details will help find the cause.',
        [CheckFailureDto.LinkRefused]:
          'The store refused the file link. Usually the link is signed for an address the store does not answer at. Check the address for browsers.',
        [CheckFailureDto.MissingRole]:
          'The media store answered in an unexpected way. The technical details will help find the cause.',
        [CheckFailureDto.NotAvailable]:
          'The media store answered in an unexpected way. The technical details will help find the cause.',
        [CheckFailureDto.NotConfigured]:
          'Some settings the media store needs are empty. Their names are in the technical details.',
        [CheckFailureDto.Refused]:
          'The store refused access. Check that the keys may use this bucket.',
        [CheckFailureDto.RejectedCredentials]:
          'The store did not accept the access keys. Check the key and secret.',
        [CheckFailureDto.ServiceAccountDisabled]:
          'The media store answered in an unexpected way. The technical details will help find the cause.',
        [CheckFailureDto.TokenRejected]:
          'The media store answered in an unexpected way. The technical details will help find the cause.',
        [CheckFailureDto.UnexpectedResponse]:
          'The media store answered in an unexpected way. The technical details will help find the cause.',
        [CheckFailureDto.Unreachable]:
          'The media store does not answer at this address. Check the address and whether the service is running.',
        [CheckFailureDto.WrongRegion]:
          'Wrong store region, so writing files and file links will not work. Check the region in the settings.',
      },
      fields: {
        bucket: 'Bucket',
        endpoint: 'Store address',
        publicEndpoint: 'Address for browsers',
        publicEndpointSame: 'Same as the store address',
        region: 'Region',
      },
      notConfigured: {
        description:
          'The store address is missing, so pictures and films cannot be added to questions. Fill in the settings on the Ankieter server.',
        title: 'The media store is not set up',
      },
      title: 'Media store',
    },
    title: 'Settings',
  },
  surveyResponse: {
    actions: {
      answerRequired: 'Answer the question to continue.',
      interrupt: 'Stop the survey',
      next: 'Next',
      previous: 'Previous',
      refuse: 'I decline',
      submit: 'Send the survey',
    },
    anonymous: "This survey is anonymous. Answers are not shown with the person's name.",
    ended: {
      refused: {
        description:
          'No answers were saved. Hand the computer back to the person who started the survey.',
        statusLabel: 'Survey ended',
        title: 'The survey has ended',
      },
      submitted: {
        description:
          'Your answers have been saved. Hand the computer back to the person who started the survey.',
        statusLabel: 'Survey sent',
        title: 'Thank you for filling in the survey',
      },
    },
    header: {
      answered: 'Answered: {{answered}} of {{total}}',
      questionOf: 'Question {{number}} of {{total}}',
    },
    instruction: 'Instructions',
    interrupt: {
      cancel: 'Back to the survey',
      confirm: 'Stop the survey',
      description: 'The answers given so far will not be saved. The survey cannot be resumed.',
      title: 'Stop the survey?',
    },
    optional: 'Optional',
    refuseFailed: 'Could not end the survey. Try again.',
    saveFailed: 'Could not save the answer. Try again.',
    submitFailed: 'Could not send the survey. Try again.',
    unavailable: {
      description:
        'The survey opens only in the browser where it was started. Go back to that browser or ask the person who started the survey for help.',
      statusLabel: 'Survey unavailable',
      title: 'This survey cannot be opened here',
    },
  },
  surveys: {
    actions: {
      copy: 'Copy',
      delete: 'Delete',
      deleteOnlyDraft: 'Only a draft can be deleted',
      edit: 'Edit',
      history: 'Change history',
      menu: 'Actions for survey {{name}}',
      preview: 'Preview',
      responses: 'Responses',
      start: 'Start the survey',
      startOnlyActive: 'Only an active survey can be started',
    },
    addSurvey: 'New survey',
    answers: {
      anonymous: 'Anonymous',
      named: 'Named',
    },
    columns: {
      actions: 'Actions',
      answers: 'Answers',
      name: 'Name',
      questions: 'Questions',
      status: 'Status',
      updatedAt: 'Changed',
    },
    copied: 'Copy "{{name}}" created.',
    copyFailed: 'Could not copy the survey. Try again.',
    copyForbidden: 'You are not allowed to copy surveys.',
    deleteDialog: {
      cancel: 'Cancel',
      confirm: 'Delete',
      description: 'The draft disappears from the list of surveys. This cannot be undone.',
      done: 'Survey "{{name}}" deleted.',
      failed: 'Could not delete the survey. Try again.',
      forbidden: 'You are not allowed to delete this survey.',
      notDraft: 'This survey has already been in use, so it cannot be deleted.',
      title: 'Delete survey "{{name}}"?',
    },
    description:
      'Surveys made of survey questions from the bank. Narrow the list with filters or search by name.',
    empty: {
      description: 'Create the first survey and add survey questions from the bank.',
      title: 'No surveys yet',
    },
    filters: {
      allCategories: 'All categories',
      allStatuses: 'Any status',
      category: 'Category',
      status: 'Status',
    },
    form: {
      actions: {
        cancel: 'Cancel',
        save: 'Save changes',
        saveDraft: 'Save draft',
      },
      activeNotice: {
        description:
          'Changes apply to answers given after you save. Answers already given stay with the questions they were given to.',
        title: 'This survey is in use',
      },
      areas: {
        description:
          'Categories of the questions in the survey. They follow from the chosen questions.',
        empty: 'Areas appear once you add questions.',
        questions: {
          count_one: '{{count}} question',
          count_other: '{{count}} questions',
        },
        shareLabel: 'Area {{name}}: {{count}} of {{total}}',
        title: 'Areas',
      },
      backToList: 'Back to surveys',
      basics: {
        anonymous: 'Anonymous answers',
        anonymousHelper: 'Reports do not tie answers to the person.',
        anonymousLocked: 'The survey has already been in use, so this setting cannot change.',
        description:
          'Name of the survey, instructions for the participant and how answers are kept.',
        instructions: 'Instructions',
        instructionsHelper: 'Shown before the survey starts. You can leave it empty.',
        name: 'Name',
        namePlaceholder: 'For example: Onboarding training feedback',
        title: 'Basics',
      },
      create: {
        description:
          'The new survey is saved as a draft. Participants do not see it until it is activated.',
        title: 'New survey',
      },
      edit: {
        description: 'Changed {{date}}, {{person}}',
        notFound: {
          description:
            'The link may have expired. Go back to the surveys and open the survey from the list.',
          title: 'Survey not found',
        },
      },
      errors: {
        anonymityLocked:
          'The survey has already been in use, so whether answers are anonymous cannot change.',
        conflict: 'Changes not saved. Refresh the page and try again.',
        forbidden: 'You are not allowed to save this survey.',
        gaps: 'Changes not saved. The survey is in use and would no longer be ready to fill in.',
        nameTaken: 'A survey with this name already exists. Enter another name.',
        notFound: 'This survey no longer exists. Go back to the surveys.',
      },
      gaps: {
        description: 'What to fix before the survey is ready to fill in.',
        noGaps: 'The survey has nothing to fix.',
        readyDraft: 'The survey is ready to activate.',
        savedOnly: 'The list shows the saved survey. Save your changes to refresh it.',
        title: 'Things to fix',
      },
      notifications: {
        created: 'Survey draft saved.',
        saved: 'Changes saved.',
        savedActive: 'Changes saved. They apply to answers given from now on.',
      },
      questions: {
        description: 'The survey asks the questions in this order. Drag a question to move it.',
        empty: {
          description:
            'Search for a survey question and add it to the list. A draft can be saved without questions.',
          title: 'The survey has no questions yet',
        },
        limit: 'The survey already has the most questions it can hold: {{max}}.',
        listLabel: 'Survey questions',
        moveDown: 'Move question {{key}} down',
        moveUp: 'Move question {{key}} up',
        needsAttention: 'Needs a fix',
        noResults: 'No matching survey questions.',
        noSummary: 'No text yet',
        remove: 'Remove question {{key}}',
        search: 'Add question',
        searchFailed: 'Could not search questions. Try again.',
        searchHint: 'Type at least 3 characters.',
        searching: 'Searching questions',
        searchPlaceholder: 'Search the question text',
        title: 'Questions',
        typeBlocked: 'Single choice or open question only',
      },
      questionSetDialog: {
        cancel: 'Cancel',
        confirm: 'Save changes',
        description:
          'Answers given before and after this change will be hard to compare. Answers already given stay with the old questions.',
        title: 'Change the questions of a survey that has been in use?',
      },
      serverErrors: {
        anonymous:
          'The survey has already been in use, so whether answers are anonymous cannot change.',
        instructions: 'Shorten the instructions to 2000 characters.',
        name: 'Check the name of the survey.',
        questions:
          'Check the questions. Each can appear once and has to be a single choice or open survey question.',
      },
    },
    gaps: {
      [SurveyGapCodeDto.NoQuestions]: 'The survey has no questions. Add at least one.',
      [SurveyGapCodeDto.QuestionNotInUse]: 'Question {{key}} has no active version.',
      [SurveyGapCodeDto.TranslationNotApproved]: 'Question {{key}} has no approved wording.',
      unknownQuestion: 'on the list',
    },
    history: {
      backToSurvey: 'Back to the survey',
      description: 'Who changed the survey and what changed. The history cannot be edited.',
      fields: {
        anonymous: 'Anonymous answers',
        copiedFrom: 'Copy of survey',
        instruction: 'Instructions',
        name: 'Name',
        questions: 'Questions',
        status: 'Status',
      },
      notFound: {
        action: 'Back to surveys',
        description:
          'The link may have expired. Go back to the surveys and open the survey from the list.',
        title: 'Survey not found',
      },
      open: 'History',
    },
    listWarning: {
      label: 'Needs attention',
      tooltip:
        'The question bank has changed since this survey went live, and some questions cannot be asked now. Open the survey and check what to fix.',
    },
    preview: {
      close: 'Close',
      description:
        'This is how a participant sees the survey. Choose a position to see the questions meant for it.',
      empty: {
        description: 'No question of the survey is meant for the chosen position.',
        title: 'No questions for this position',
      },
      error: {
        description: 'Try again in a moment.',
        title: 'Could not show the preview',
      },
      notFound: {
        description: 'The survey may have been deleted. Close this window and refresh the list.',
        title: 'Survey not found',
      },
      open: 'Preview',
      position: 'Participant position',
      positionPlaceholder: 'All positions',
      questionNumber: 'Question {{number}} of {{total}}',
      refused: 'The preview cannot be shown',
      saveFirst: 'Save your changes first. The preview shows the saved survey.',
      title: 'Survey preview: {{name}}',
    },
    responses: {
      backToSurveys: 'Back to the survey list',
      columns: {
        endedAt: 'Ended',
        person: 'Person',
        position: 'Position',
        startedAt: 'Started',
        startedBy: 'Started by',
        state: 'Status',
      },
      details: {
        anonymity: { anonymous: 'Anonymous', named: 'Named' },
        answers: {
          anonymous: 'This survey is anonymous, so the answers of one person are not shown.',
          answeredAt: 'Answered {{time}}',
          emptyInProgress: 'The person has not finished the survey yet.',
          emptyRefused: 'The survey was declined or stopped, so no answers were saved.',
          noAnswer: 'No answer',
          questionNumber: 'Question {{number}}',
          title: 'Answers',
        },
        backToList: 'Back to the responses',
        fields: {
          anonymity: 'Survey',
          endedAt: 'Ended',
          position: 'Position',
          sapNumber: 'SAP number',
          startedAt: 'Started',
          startedBy: 'Started by',
        },
        forbidden: {
          action: 'Back to the responses',
          description: 'You do not have access to this response.',
          title: 'Response unavailable',
        },
        notFound: {
          action: 'Back to the responses',
          description: 'The response may have been removed, or the link is wrong.',
          title: 'Response not found',
        },
        notYet: 'Not yet',
        summaryLabel: 'Summary',
      },
      empty: {
        description: 'Start the survey from the survey list, and the response will show up here.',
        title: 'Nobody has filled in this survey yet',
      },
      filters: {
        allStatuses: 'All statuses',
        sapNumber: 'SAP number',
        sapNumberPlaceholder: 'Enter the SAP number',
        startedFrom: 'Started from',
        startedTo: 'Started to',
        status: 'Status',
        statuses: {
          [SurveyResponseStatusDto.InProgress]: 'In progress',
          [SurveyResponseStatusDto.Refused]: 'Declined or stopped',
          [SurveyResponseStatusDto.Submitted]: 'Filled in',
        },
      },
      state: {
        [SurveyResponseStateEnum.IN_PROGRESS]: 'In progress',
        [SurveyResponseStateEnum.INTERRUPTED]: 'Stopped',
        [SurveyResponseStateEnum.REFUSED]: 'Declined',
        [SurveyResponseStateEnum.SUBMITTED]: 'Filled in',
      },
      tableLabel: 'Survey responses',
      title: 'Responses',
    },
    search: {
      label: 'Search by name',
    },
    startResponse: {
      cancel: 'Cancel',
      confirm: 'Start the survey',
      description:
        'Enter the details of the person who will fill in the survey. The survey opens in this window, without the app menu.',
      failed: 'Could not start the survey. Try again.',
      gapsTitle: 'The survey is not ready for this position',
      name: 'First and last name',
      notActive: 'This survey is no longer active, so it cannot be started.',
      notReady:
        'Some questions for this position are not ready. Fix them or choose another position.',
      position: 'Position held',
      positionPlaceholder: 'Choose a position',
      sapNumber: 'SAP number',
      title: 'Start the survey: {{name}}',
    },
    status: {
      [SurveyStatusDto.Active]: 'Active',
      [SurveyStatusDto.Draft]: 'Draft',
      [SurveyStatusDto.Retired]: 'Retired',
    },
    statusDialog: {
      actions: {
        [StatusActionEnum.ACTIVATE]: {
          confirm: 'Activate',
          description:
            'Participants will be able to fill in the survey. After that, whether answers are anonymous cannot change.',
          done: 'Survey "{{name}}" is active.',
          refusedTitle: 'The survey cannot be activated',
          title: 'Activate survey "{{name}}"?',
        },
        [StatusActionEnum.RESTORE]: {
          confirm: 'Restore',
          description: 'The survey becomes active again and participants can fill it in.',
          done: 'Survey "{{name}}" restored.',
          refusedTitle: 'The survey cannot be restored',
          title: 'Restore survey "{{name}}"?',
        },
        [StatusActionEnum.RETIRE]: {
          confirm: 'Retire',
          description:
            'Nobody will be able to fill in this survey. Answers stay. You can restore the survey later.',
          done: 'Survey "{{name}}" retired.',
          refusedTitle: 'The survey cannot be retired',
          title: 'Retire survey "{{name}}"?',
        },
      },
      cancel: 'Cancel',
      close: 'Close',
      failed: 'Could not change the status of the survey. Try again.',
      forbidden: 'You are not allowed to change the status of this survey.',
      nameTaken:
        'Another survey that is not retired already has this name. Rename one of them, then restore this survey.',
      openSurvey: 'Open survey',
      refusedIntro: 'Fix these first, then try again:',
    },
    tableLabel: 'Surveys',
    title: 'Surveys',
  },
  tests: {
    actions: {
      copy: 'Copy',
      delete: 'Delete',
      deleteOnlyDraft: 'Only a draft can be deleted',
      edit: 'Edit',
      history: 'Change history',
      menu: 'Actions for test {{name}}',
      runExam: 'Run the exam',
      runExamOnlyActive: 'Only an active exam can be run',
      sample: 'Sample draw',
      start: 'Start the test',
      startOnlyActive: 'Only an active test can be started',
    },
    addTest: 'New test',
    columns: {
      actions: 'Actions',
      length: 'Questions',
      name: 'Name',
      parts: 'Parts',
      purpose: 'Purpose',
      status: 'Status',
      updatedAt: 'Changed',
    },
    copied: 'Copy "{{name}}" created.',
    copyFailed: 'Could not copy the test. Try again.',
    deleteDialog: {
      cancel: 'Cancel',
      confirm: 'Delete',
      description: 'The draft disappears from the list of tests. This cannot be undone.',
      done: 'Test "{{name}}" deleted.',
      failed: 'Could not delete the test. Try again.',
      forbidden: 'You are not allowed to delete this test.',
      notDraft: 'This test has already been in use, so it cannot be deleted.',
      title: 'Delete test "{{name}}"?',
    },
    description:
      'Knowledge tests and exams made of questions from the bank. Narrow the list with filters or search by name.',
    empty: {
      description: 'Create the first test and add parts with questions from the bank.',
      title: 'No tests yet',
    },
    filters: {
      allCategories: 'All categories',
      allPurposes: 'Any purpose',
      allStatuses: 'Any status',
      category: 'Category',
      purpose: 'Purpose',
      status: 'Status',
    },
    form: {
      actions: {
        cancel: 'Cancel',
        save: 'Save changes',
        saveDraft: 'Save draft',
      },
      backToList: 'Back to tests',
      basics: {
        description: 'Name and purpose of the test, and instructions for the participant.',
        instructions: 'Instructions',
        instructionsHelper: 'Shown before the test starts. You can leave it empty.',
        name: 'Name',
        namePlaceholder: 'For example: Stage test, customer service',
        purpose: 'Purpose',
        purposePlaceholder: 'Choose the purpose',
        title: 'Basics',
      },
      create: {
        description:
          'The new test is saved as a draft. Participants do not see it until it is activated.',
        title: 'New test',
      },
      edit: {
        activeNotice: {
          description:
            'Changes apply to attempts started after you save. Attempts already started stay as they are.',
          title: 'This test is in use',
        },
        description: 'Changed {{date}}, {{person}}',
        notFound: {
          description:
            'The link may have expired. Go back to the tests and open the test from the list.',
          title: 'Test not found',
        },
      },
      errors: {
        forbidden: 'You are not allowed to save this test.',
        gaps: 'Changes not saved. The test is in use and would no longer be ready to take.',
        nameTaken: 'A test with this name already exists. Enter another name.',
        notFound: 'This test no longer exists. Go back to the tests.',
      },
      notifications: {
        created: 'Test draft saved.',
        saved: 'Changes saved.',
        savedActive: 'Changes saved. They apply to attempts started from now on.',
      },
      parts: {
        add: 'Add part',
        addLimit: 'A test can have at most {{max}} parts.',
        category: 'Category',
        categoryPlaceholder: 'Choose a category',
        chosen: {
          blocked: 'Practical exam only',
          empty: 'No questions chosen yet.',
          limit: 'This part already has the most questions it can hold: {{max}}.',
          noResults: 'No matching test questions.',
          noSummary: 'No text yet',
          remove: 'Remove question {{key}}',
          search: 'Add question',
          searchFailed: 'Could not search questions. Try again.',
          searchHint: 'Type at least 3 characters.',
          searching: 'Searching questions',
          searchPlaceholder: 'Search the question text',
        },
        description:
          'The test goes through the parts in this order. Each part takes questions from a category or from a list of chosen questions.',
        empty: {
          description:
            'Add a part to say which questions the test is made of. A draft can be saved without parts.',
          title: 'The test has no parts yet',
        },
        moveDown: 'Move part {{number}} down',
        moveUp: 'Move part {{number}} up',
        questionCount: 'Number of questions',
        remove: 'Remove part {{number}}',
        title: 'Parts',
      },
      pool: {
        available: 'Available',
        description:
          'How many questions each part can draw from. The pool updates as you change the purpose and the parts.',
        length: 'Test length',
        needed: 'Needed',
        noQuestions: 'No questions',
        notReady: 'Not enough questions',
        positions: {
          description:
            'How many questions a person in each position can get. This is information, not a condition for activation.',
          other: 'Other positions',
          part: 'Part {{number}}',
          position: 'Position',
          ready: 'Readiness',
          title: 'Pool by position',
        },
        practicalOnlyHint:
          'Questions marked by an examiner or worth other than 1 point fit only a practical exam.',
        progressLabel: '{{available}} of {{needed}} questions available',
        questions: {
          count_one: '{{count}} question',
          count_other: '{{count}} questions',
        },
        ready: 'Enough questions',
        shortBy: 'Short by {{count}}',
        skipped: {
          [PoolSkipReasonDto.ChosenInAnotherPart]: {
            count_one: '{{count}} question chosen in another part',
            count_other: '{{count}} questions chosen in another part',
          },
          [PoolSkipReasonDto.MarkedByExaminer]: {
            count_one: '{{count}} question marked by an examiner',
            count_other: '{{count}} questions marked by an examiner',
          },
          [PoolSkipReasonDto.NoApprovedWording]: {
            count_one: '{{count}} question without approved wording',
            count_other: '{{count}} questions without approved wording',
          },
          [PoolSkipReasonDto.NotInUse]: {
            count_one: '{{count}} question without an active version',
            count_other: '{{count}} questions without an active version',
          },
          [PoolSkipReasonDto.NotOnePoint]: {
            count_one: '{{count}} question worth other than 1 point',
            count_other: '{{count}} questions worth other than 1 point',
          },
          [PoolSkipReasonDto.ScoredBySystem]: {
            count_one: '{{count}} question scored automatically',
            count_other: '{{count}} questions scored automatically',
          },
          [PoolSkipReasonDto.SurveyQuestion]: {
            count_one: '{{count}} survey question',
            count_other: '{{count}} survey questions',
          },
        },
        skippedIntro: 'Left out: {{reasons}}.',
        states: {
          error: {
            description: 'Try again in a moment.',
            title: 'Could not count the pool',
          },
          forbidden: {
            description: 'You are not allowed to see the question pool of this test.',
            title: 'No access to the pool',
          },
          incomplete: {
            description:
              'In every part, choose a category and the number of questions, or add questions. The pool updates on its own.',
            title: 'Complete the parts',
          },
          noParts: {
            description: 'Add a part to see how many questions it can draw from.',
            title: 'The test has no parts yet',
          },
          noPurpose: {
            description: 'The pool depends on the purpose of the test. Choose it under Basics.',
            title: 'Choose the purpose first',
          },
          rejected: {
            description: 'Save the test to see which fields need a change.',
            title: 'Could not count the pool for these settings',
          },
        },
        sufficient: 'Enough',
        title: 'Question pool',
        warnings: {
          [TestPoolWarningCodeDto.FixedSetWithReview]:
            'Participants see their mistakes after the test, and this part asks the same questions every time. The summary shows the mistakes from this part without the correct answers.',
          [TestPoolWarningCodeDto.PracticalExamVaries]:
            'This part draws fewer questions than it has, so participants get different exams.',
          [TestPoolWarningCodeDto.SmallPoolWithReview]:
            'Participants see their mistakes after the test, and this part has fewer than twice the questions it draws. Another attempt brings back mostly the same questions.',
        },
      },
      rules: {
        allowGoingBack: 'Going back to questions',
        allowGoingBackHelper: 'The participant can return to earlier questions during the test.',
        attemptLimit: 'Attempt limit',
        attemptLimits: {
          add: 'Add exception',
          description: 'A position can have a different attempt limit than other participants.',
          limit: 'Attempt limit',
          position: 'Position',
          positionPlaceholder: 'Search by name or number',
          remove: 'Remove exception {{number}}',
          title: 'Attempt limit exceptions',
        },
        description: 'Passing, time and attempts.',
        lockAfterPass: 'Lock after passing',
        lockAfterPassHelper: 'Once passed, the participant cannot take the test again.',
        mistakeReview: 'Mistake review after the test',
        noLimitHelper: 'Leave empty for no limit.',
        passThreshold: 'Pass threshold (%)',
        passThresholdHelper: 'Leave empty for a test without passing, such as a diagnosis.',
        retryAdvice: 'Wait before another attempt',
        retryAdviceHelper: 'Advice for the participant. It does not block another attempt.',
        retryAdvicePlaceholder: 'For example: 5 days',
        timeLimit: 'Time limit (min)',
        title: 'Rules',
      },
      serverErrors: {
        attemptLimit: 'Enter 1 to 99 attempts or leave the field empty.',
        attemptLimitPosition: 'Choose an active position. Each position can have one exception.',
        attemptLimits: 'Check the attempt limit exceptions.',
        instructions: 'Shorten the instructions to 2000 characters.',
        mistakeReview: 'Choose how mistakes are reviewed.',
        name: 'Check the name of the test.',
        partCategory: 'Choose a category that is in use and not in another part.',
        partQuestionCount: 'Enter 1 to 200 questions.',
        partQuestions:
          'Check the chosen questions. Each can appear in a test once, and questions graded by an examiner fit only a practical exam.',
        parts: 'Check the parts of the test. A test can have at most 20 parts.',
        passThreshold: 'Enter a threshold from 1 to 100 or leave the field empty.',
        purpose: 'Choose the purpose of the test.',
        retryAdvice: 'Shorten the advice to 100 characters.',
        timeLimitMinutes: 'Enter 1 to 600 minutes or leave the field empty.',
      },
    },
    history: {
      backToTest: 'Back to the test',
      description: 'Who changed the test and what changed. The history cannot be edited.',
      fields: {
        allowGoingBack: 'Going back to questions',
        attemptLimit: 'Attempt limit',
        attemptLimits: 'Attempt limits by position',
        copiedFrom: 'Copy of test',
        instruction: 'Instructions',
        lockAfterPass: 'Lock after passing',
        mistakeReview: 'Mistake review after the test',
        name: 'Name',
        parts: 'Parts',
        passThreshold: 'Pass threshold',
        purpose: 'Purpose',
        retryAdvice: 'Wait before another attempt',
        status: 'Status',
        timeLimitMinutes: 'Time limit',
      },
      notFound: {
        action: 'Back to tests',
        description:
          'The link may have expired. Go back to the tests and open the test from the list.',
        title: 'Test not found',
      },
      open: 'History',
      values: {
        minutes: '{{value}} min',
        percent: '{{value}}%',
      },
    },
    listWarning: {
      label: 'Questions missing',
      tooltip:
        'The question bank has changed since this test went live, and some parts now lack questions. Open the test and check the question pool.',
    },
    mistakeReview: {
      [MistakeReviewDto.Mistakes]: 'Show mistakes',
      [MistakeReviewDto.MistakesWithExplanations]: 'Show mistakes with explanations',
      [MistakeReviewDto.None]: 'No review',
    },
    partKind: {
      [TestPartKindDto.ChosenQuestions]: {
        description: 'Questions picked from the bank.',
        label: 'Chosen questions',
      },
      [TestPartKindDto.Draw]: {
        description: 'A set number of questions drawn from a category.',
        label: 'Draw from a category',
      },
      [TestPartKindDto.WholeCategory]: {
        description: 'Every question in a category.',
        label: 'Whole category',
      },
    },
    purpose: {
      [TestPurposeDto.EntryTest]: 'Entry knowledge test',
      [TestPurposeDto.PracticalExam]: 'Practical exam',
      [TestPurposeDto.StageTest]: 'Stage test',
      [TestPurposeDto.TheoryExam]: 'Theory exam',
    },
    sample: {
      close: 'Close',
      description:
        'This is how a participant could see the test. Every draw gives a new set, and nothing is saved.',
      emptyPart: 'This part gives no questions in this draw.',
      error: {
        description: 'Try again in a moment.',
        title: 'Could not draw the test',
      },
      notFound: {
        description: 'The test may have been deleted. Close this window and refresh the list.',
        title: 'Test not found',
      },
      open: 'Sample draw',
      part: 'Part {{number}}',
      position: 'Participant position',
      positionPlaceholder: 'All positions',
      questionNumber: 'Question {{number}} of {{total}}',
      redraw: 'Draw again',
      refused: 'The test cannot be drawn',
      saveFirst: 'Save your changes first. The draw uses the saved test.',
      showCorrect: 'Show correct answers',
      title: 'Sample draw: {{name}}',
    },
    search: {
      label: 'Search by name',
    },
    startAttempt: {
      alreadyPassed: 'This person has already passed this test.',
      blocked: {
        back: 'Back',
        limit: {
          confirm: 'Grant an attempt and start the test',
          confirmExam: 'Grant an attempt and run the exam',
          description:
            'This person has used every attempt at this test. You can grant an extra attempt and start the test straight away.',
          descriptionExam:
            'This person has used every attempt at this exam. You can grant an extra attempt and run the exam straight away.',
          failed: 'Could not grant the attempt. Try again.',
          reason: 'Reason for the extra attempt',
          reasonPlaceholder: 'Briefly say why.',
          title: 'Every attempt has been used',
        },
        locked: {
          confirm: 'Unlock and start the test',
          confirmExam: 'Unlock and run the exam',
          description:
            "This person's attempts at this test are locked. You can remove the lock and start the test straight away.",
          descriptionExam:
            "This person's attempts at this exam are locked. You can remove the lock and run the exam straight away.",
          failed: 'Could not unlock the attempts. Try again.',
          title: 'Attempts are locked',
        },
      },
      cancel: 'Cancel',
      confirm: 'Start the test',
      description:
        'Enter the details of the person taking the test. The test opens in this window, without the app menu.',
      exam: {
        alreadyPassed: 'This person has already passed this exam.',
        confirm: 'Run the exam',
        description:
          'Enter the details of the person being examined. The examiner card opens next, and each grade is saved as you give it.',
        failed: 'Could not run the exam. Try again.',
        notReady:
          'There are not enough questions to draw this exam for the chosen position. Add questions or choose another position.',
        title: 'Run the exam: {{name}}',
      },
      failed: 'Could not start the test. Try again.',
      name: 'First and last name',
      notReady:
        'There are not enough questions to draw this test for the chosen position. Add questions or choose another position.',
      position: 'Position held',
      positionPlaceholder: 'Choose a position',
      sapNumber: 'SAP number',
      targetPosition: 'Position the test is for',
      targetPositionHelper: 'Leave it empty if it is the position the person holds.',
      targetPositionPlaceholder: 'Same as the position held',
      title: 'Start the test: {{name}}',
    },
    status: {
      [TestStatusDto.Active]: 'Active',
      [TestStatusDto.Draft]: 'Draft',
      [TestStatusDto.Retired]: 'Retired',
    },
    statusDialog: {
      actions: {
        [StatusActionEnum.ACTIVATE]: {
          confirm: 'Activate',
          description:
            'Participants will be able to start the test. Changes saved later apply only to new attempts.',
          done: 'Test "{{name}}" is active.',
          refusedTitle: 'The test cannot be activated',
          title: 'Activate test "{{name}}"?',
        },
        [StatusActionEnum.RESTORE]: {
          confirm: 'Restore',
          description: 'The test becomes active again and participants can start it.',
          done: 'Test "{{name}}" restored.',
          refusedTitle: 'The test cannot be restored',
          title: 'Restore test "{{name}}"?',
        },
        [StatusActionEnum.RETIRE]: {
          confirm: 'Retire',
          description:
            'Nobody will be able to start this test. Attempts and results stay. You can restore the test later.',
          done: 'Test "{{name}}" retired.',
          refusedTitle: 'The test cannot be retired',
          title: 'Retire test "{{name}}"?',
        },
      },
      cancel: 'Cancel',
      categoryNotInUseAnyPart:
        'One of the parts draws questions from a category that is turned off. Pick another category for it.',
      close: 'Close',
      failed: 'Could not change the status of the test. Try again.',
      forbidden: 'You are not allowed to change the status of this test.',
      gaps: {
        [TestGapCodeDto.CategoryNotInUse]:
          'Part {{number}} draws questions from a category that is turned off. Pick another category for it.',
        [TestGapCodeDto.InstructionMissing]: 'The instructions are missing.',
        [TestGapCodeDto.NoParts]: 'The test has no parts. Add at least one.',
        [TestGapCodeDto.PoolTooSmall]:
          'Part {{number}} does not have enough questions in its pool.',
      },
      nameTaken:
        'Another test that is not retired already has this name. Rename one of them, then restore this test.',
      openTest: 'Open test',
      poolTooSmallAnyPart: 'One of the parts does not have enough questions in its pool.',
      refusedIntro: 'Fix these first, then try again:',
    },
    tableLabel: 'Tests',
    title: 'Tests',
  },
};
